/**
 * バックエンド API クライアント
 * 開発・本番とも /api を利用（Vite または Express がバックエンドへプロキシ）
 */
const API_BASE = import.meta.env.VITE_API_BASE ?? '/api';

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface ApiError {
  error: {
    code: string;
    message: string;
    traceId?: string;
    details?: Record<string, unknown>;
  };
}

/**
 * API エラー（ステータスコード付き）。画面で 404/500 などを区別して表示するために使用。
 * 2026-05-30 から BE が構造化エラー（error_code + details）を返すケースが増えたため、
 * body / errorCode / details も保持する
 */
export class RequestError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body?: unknown,
    public readonly errorCode?: string | null,
    public readonly details?: unknown
  ) {
    super(message);
    this.name = 'RequestError';
  }
}

/** `{ error: { message } }` または FastAPI 形式の `{ detail: ... }` から表示用メッセージを取り出す */
function extractApiErrorMessage(data: unknown, fallback: string): string {
  if (!data || typeof data !== 'object') return fallback;
  const o = data as Record<string, unknown>;

  if (o.error && typeof o.error === 'object' && o.error !== null) {
    const e = o.error as Record<string, unknown>;
    if (typeof e.message === 'string' && e.message.trim()) return e.message.trim();
  }

  const detail = o.detail;
  if (typeof detail === 'string' && detail.trim()) return detail.trim();
  /*
   * detail がオブジェクトの形（BE #90〜）:
   *   { detail: { error_code, message, hint } }
   * message は利用者向け、hint は運用者向けなので message だけを画面に出す。
   * hint には「status=active のプロンプトを…」のような内部設定の説明が入るため、
   * ここで取り違えると利用者に運用情報が漏れる。
   */
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const dm = (detail as Record<string, unknown>).message;
    if (typeof dm === 'string' && dm.trim()) return dm.trim();
  }
  if (Array.isArray(detail)) {
    const parts = detail
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object' && item !== null && 'msg' in item) {
          const m = (item as { msg?: unknown }).msg;
          return typeof m === 'string' ? m : '';
        }
        return '';
      })
      .filter((s) => s.length > 0);
    if (parts.length > 0) return parts.join(' ');
  }

  if (typeof o.message === 'string' && o.message.trim()) return o.message.trim();

  return fallback;
}

/**
 * 保護された API が 401 のときのフロント側の扱い（バックエンドと揃える前提）
 *
 * 1. 保存している refresh で POST /v1/auth/refresh を試す（同一時刻に複数 401 でも refresh は 1 回にまとめる）
 * 2. 成功したら新しい access（とレスポンスに含まれる refresh）を保存し、失敗したリクエストを 1 回だけ再送する
 * 3. refresh トークンが無い場合は再試行せず 401 のまま（画面側の 401 処理・PrivateRoute でログインへ）
 * 4. POST /v1/auth/refresh が失敗したらセッション削除 → ログインへ
 *
 * /v1/auth/login・/v1/auth/refresh・/v1/auth/logout では再試行しない（ループ防止）
 */
let authRefreshHandler: (() => Promise<string | null>) | null = null;
let authRefreshInFlight: Promise<string | null> | null = null;

export function setApiAuthRefresh(handler: (() => Promise<string | null>) | null) {
  authRefreshHandler = handler;
}

function shouldSkipAuthRefreshRetry(path: string): boolean {
  return (
    path.includes('/v1/auth/login') ||
    path.includes('/v1/auth/refresh') ||
    path.includes('/v1/auth/logout')
  );
}

async function runAuthRefreshOnce(): Promise<string | null> {
  if (!authRefreshHandler) return null;
  if (!authRefreshInFlight) {
    authRefreshInFlight = (async () => {
      try {
        return await authRefreshHandler!();
      } finally {
        authRefreshInFlight = null;
      }
    })();
  }
  return authRefreshInFlight;
}

async function request<T>(
  path: string,
  options: {
    method?: Method;
    body?: unknown;
    token?: string | null;
    idempotencyKey?: string;
    timeoutMs?: number;
    _authRetry?: boolean;
    /** 追加のヘッダー（例: 招待の本文取得の X-Invite-Token） */
    headers?: Record<string, string>;
  } = {}
): Promise<T> {
  const { method = 'GET', body, token, idempotencyKey, timeoutMs, _authRetry, headers: extraHeaders } = options;
  const url = path.startsWith('http') ? path : `${API_BASE.replace(/\/$/, '')}${path.startsWith('/') ? path : `/${path}`}`;
  // FormData（ファイルの送信）は、Content-Type をブラウザに任せる（境界文字列を付けさせるため）
  const isForm = typeof FormData !== 'undefined' && body instanceof FormData;
  const headers: Record<string, string> = isForm ? {} : { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;
  if (extraHeaders) Object.assign(headers, extraHeaders);

  const controller = timeoutMs != null ? new AbortController() : undefined;
  const timeoutId =
    controller && timeoutMs
      ? setTimeout(() => controller.abort(), timeoutMs)
      : undefined;

  let res: Response;
  try {
    res = await fetch(url, {
      method,
      headers,
      body: isForm ? (body as FormData) : body != null ? JSON.stringify(body) : undefined,
      credentials: 'same-origin',
      signal: controller?.signal,
    });
  } catch (e) {
    if (e instanceof Error && e.name === 'AbortError') {
      throw new RequestError('リクエストがタイムアウトしました。しばらくしてから再度お試しください。', 408);
    }
    throw e;
  } finally {
    if (timeoutId != null) clearTimeout(timeoutId);
  }

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (
      res.status === 401 &&
      !_authRetry &&
      !shouldSkipAuthRefreshRetry(path)
    ) {
      const newAccess = await runAuthRefreshOnce();
      if (newAccess) {
        return request<T>(path, {
          method,
          body,
          token: newAccess,
          idempotencyKey,
          timeoutMs,
          _authRetry: true,
        });
      }
    }
    const fallback = res.statusText || 'Request failed';
    // 構造化エラー（2026-05-30〜）: BE が error_code / details を返す場合は同梱
    const errCode = extractErrorCode(data);
    const details = extractErrorDetails(data);
    throw new RequestError(
      extractApiErrorMessage(data, fallback),
      res.status,
      data,
      errCode,
      details
    );
  }
  return data as T;
}

function extractErrorCode(data: unknown): string | null {
  if (!data || typeof data !== 'object') return null;
  const o = data as Record<string, unknown>;
  if (typeof o.error_code === 'string') return o.error_code;
  // { detail: { error_code, message, hint } } の形（BE #90〜）
  if (o.detail && typeof o.detail === 'object' && !Array.isArray(o.detail)) {
    const dc = (o.detail as Record<string, unknown>).error_code;
    if (typeof dc === 'string') return dc;
  }
  if (o.error && typeof o.error === 'object' && o.error !== null) {
    const e = o.error as Record<string, unknown>;
    if (typeof e.code === 'string') return e.code;
  }
  return null;
}

function extractErrorDetails(data: unknown): unknown {
  if (!data || typeof data !== 'object') return undefined;
  const o = data as Record<string, unknown>;
  if (o.details !== undefined) return o.details;
  if (o.error && typeof o.error === 'object' && o.error !== null) {
    const e = o.error as Record<string, unknown>;
    if (e.details !== undefined) return e.details;
  }
  return undefined;
}

export const api = {
  get: <T>(path: string, token?: string | null, headers?: Record<string, string>) =>
    request<T>(path, { method: 'GET', token, headers }),

  post: <T>(
    path: string,
    body?: unknown,
    token?: string | null,
    idempotencyKey?: string,
    timeoutMs?: number
  ) => request<T>(path, { method: 'POST', body, token, idempotencyKey, timeoutMs }),

  put: <T>(path: string, body?: unknown, token?: string | null, idempotencyKey?: string) =>
    request<T>(path, { method: 'PUT', body, token, idempotencyKey }),

  delete: <T>(path: string, token?: string | null, idempotencyKey?: string) =>
    request<T>(path, { method: 'DELETE', token, idempotencyKey }),
};

// 認証 API（要件: POST /v1/auth/login, POST /v1/auth/logout, POST /v1/auth/refresh）
export interface LoginResponse {
  access_token: string;
  refresh_token?: string;
  expires_in?: number;
  token_type?: string;
}

/**
 * POST /v1/auth/accept-invite の各同意レコード（任意指定）
 * document_id / version は事前に管理者から共有された値、または
 * ログイン後に GET /v1/consent/master-modes で取得した値を使う
 */
export interface AcceptInviteAgreement {
  document_id: string;
  version: string;
}

export interface AcceptInviteRequest {
  invite_token: string;
  /** パスワード（8文字以上） */
  password: string;
  /** 受諾と同時に同意する文書一覧（任意）。省略時は同意レコードなしで User のみ作成される */
  tos_agreements?: AcceptInviteAgreement[];
}

export interface AcceptInviteRecordedConsent {
  consent_id: string;
  document_id: string;
  purpose: string;
  data_type: string;
  version: string;
}

export interface AcceptInviteResponse {
  status: 'accepted';
  user_id: string;
  recorded_consents: AcceptInviteRecordedConsent[];
}

/** GET /v1/auth/me。表示名が空なら display_name は null */
export interface MeResponse {
  user_id: string;
  org_id: string;
  role: string;
  display_name?: string | null;
  email?: string | null;
}

export const authApi = {
  login: (tenantCode: string, email: string, password: string) =>
    api.post<LoginResponse>('/v1/auth/login', { tenant_code: tenantCode, email, password }),

  logout: (token: string) =>
    api.post<unknown>('/v1/auth/logout', undefined, token),

  refresh: (refreshToken: string) =>
    api.post<LoginResponse>('/v1/auth/refresh', { refresh_token: refreshToken }),

  /**
   * 招待受諾（公開エンドポイント・認証不要）。
   * tos_agreements は任意。全 or-nothing トランザクションで、
   * 1件でも不正な document_id / version があると User も作成されない
   */
  acceptInvite: (body: AcceptInviteRequest) =>
    api.post<AcceptInviteResponse>('/v1/auth/accept-invite', body),
  /**
   * パスワードの設定の URL（管理者の「パスワードをリセット」で本人のメールに届く。1 日有効・1 回だけ）から、
   * 本人が新しいパスワードを決める（課題 #101）。無効・期限切れは 401、使用済みは 400
   */
  setPassword: (body: { token: string; password: string }) =>
    api.post<{ status: string }>('/v1/auth/set-password', body),

  /** ログイン中の本人の情報。画面の上部にアカウント名を出すのに使う（#174） */
  me: (token: string | null) => api.get<MeResponse>('/v1/auth/me', token),
};

// --- 従業員向け API（EMP-S01 ダッシュボード用）---

/** 前回比較トレンド（旧スキーマ。2026-04-22 から day_over_day に置換） */
export interface Trend {
  label: string;
  direction: '↑' | '↓' | '→';
}

/**
 * 4 ドメインスコア（仕様書 0422 §10.1）。
 * UI ラベルは「活力 / ストレス傾向 / 集中 / 反応の強さ」。
 * cognition_mode は補助表示扱い（感情優位 / 中庸 / 論理優位）
 */
export interface DailyReportDomains {
  vitality: number;
  tension: number;
  cognition: number;
  cognition_mode: '感情優位' | '中庸' | '論理優位' | string;
  emotion_stability: number;
}

/** calm（対話のトーン）。level 1=red / 2=yellow / 3=green。atmosphere_avg は平均値 */
export interface DailyReportCalm {
  level: 1 | 2 | 3;
  label: 'red' | 'yellow' | 'green' | string;
  atmosphere_avg: number;
}

/** 不安クラスタ（rule_a 検出件数 + 該当セグメント） */
export interface DailyReportAnxietyClusters {
  rule_a_count: number;
  triggered_segments: unknown[];
}

/** 前日比（仕様 §9）。direction が null のときは比較不能 */
export interface DailyReportDayOverDay {
  /** 前日比変化率 (%). BE が算出不可の場合は undefined で返るためガード必須 */
  delta_pct?: number;
  direction: 'up' | 'down' | 'flat' | null;
}

/**
 * コールドスタート段階（仕様 §9 5 段階表）。
 * show_* フラグで UI 要素ごとに表示可否を制御する
 */
export interface DailyReportColdStart {
  stage: 'day_1' | 'day_2_6' | 'day_7_13' | 'day_14_27' | 'day_28_plus' | string;
  day_count: number;
  show_4_domains: boolean;
  show_calm: boolean;
  show_day_over_day: boolean;
  show_weekly_trend: boolean;
  show_monthly_mcs: boolean;
}

/** Pattern A 通知（id=99 は相談導線） */
export interface DailyReportPatternANotification {
  id: number;
  condition: string;
  text: string;
}

/** 2026-04-22 以降の日次レポート content_json スキーマ（Phase A） */
export interface DailyReportContentNew {
  domains?: DailyReportDomains;
  calm?: DailyReportCalm;
  anxiety_clusters?: DailyReportAnxietyClusters;
  day_over_day?: DailyReportDayOverDay;
  cold_start?: DailyReportColdStart;
  pattern_a_notification?: DailyReportPatternANotification | null;
  voice_count?: number;
  comment?: string | null;
}

/**
 * 日次レポート（FE 内部の正規化済み形）。
 * 新旧スキーマを 1 つの型に保持し、`schema` で判別する。
 * 既存コードは comment / trends / condition で旧形式表示を、
 * 新規コードは domains / calm / day_over_day で新形式表示を行う
 */
/**
 * 日次レポートに入る、MCS の表示までの残り（課題 #61）。直近 28 日の実績で、録音のたびに更新される。
 * estimated_seconds_to_display: 1 以上は目安の残り秒数、0 は条件を満たした、null は分からない。
 */
export interface DailyMcsProgress {
  estimated_seconds_to_display?: number | null;
  segments_to_display?: number | null;
  recordings_to_display?: number | null;
  segment_count?: number | null;
  voice_count?: number | null;
}

function toDailyMcsProgress(raw: unknown): DailyMcsProgress | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
  return {
    estimated_seconds_to_display: num(o.estimated_seconds_to_display),
    segments_to_display: num(o.segments_to_display),
    recordings_to_display: num(o.recordings_to_display),
    segment_count: num(o.segment_count),
    voice_count: num(o.voice_count),
  };
}

export interface DailyReport {
  date: string;
  /** このまとめに入っている最後の録音の時刻（UTC）。BE が入る前に作ったまとめは null（#174） */
  lastRecordedAt?: string | null;
  /** MCS の表示までの残り（直近 28 日）。古いレポートなど、無ければ null（#61） */
  mcsProgress?: DailyMcsProgress | null;
  /** 'new' = 2026-04-22〜の新スキーマ、'legacy' = 旧スキーマ */
  schema: 'new' | 'legacy';
  comment: string;
  transcription?: string | null;

  // --- 新スキーマ（schema='new' 時のみ意味あり）---
  domains?: DailyReportDomains;
  calm?: DailyReportCalm;
  anxietyClusters?: DailyReportAnxietyClusters;
  dayOverDay?: DailyReportDayOverDay;
  coldStart?: DailyReportColdStart;
  patternANotification?: DailyReportPatternANotification | null;
  voiceCount?: number;

  // --- 旧スキーマ（schema='legacy' 時のみ意味あり、後方互換）---
  trends?: Trend[];
  promptId?: string | null;
  promptName?: string | null;
  condition?: string | null;
  condition_emoji?: string | null;
  highlightedParameters?: Array<{ key: string; label: string; value: number }> | null;
  syntheticParameters?: Array<{ id: string; label: string; value: number }> | null;
}

/** GET /v1/daily-reports/today の生レスポンス（未作成時は report=null で 200） */
export interface DailyReportTodayResponse {
  report_date: string;
  /** 次にまとめを作り始める時刻の目安（UTC）（#174）。作り始めてからコメントの生成に時間がかかる */
  next_generation_at?: string | null;
  report: {
    report_date: string;
    prompt_version?: string;
    model_version?: string;
    engine_version?: string | null;
    content?: Record<string, unknown>;
  } | null;
}

/**
 * 月次論文版 MCS 算出モード（仕様 0422 §6 / Phase B、2026-04-26 時点）。
 * - `standard`: 28 日分以上の録音があり、論文標準算出
 * - `short_reference`: 50% 以上 28 日未満の参考値（参考表示扱い）
 * - `hold`: 録音 50% 未満、MCS 未算出
 */
export type MCSModeApplied = 'standard' | 'short_reference' | 'hold' | string;

/**
 * Cutoff バージョン: `v1`=0.334（正式版）/ `v0`=0.313（フィールドテスト暫定版）。
 * 2026-10-23 の BE PR #186 で `formal_0_334`→`v1`、`temp_0_313`→`v0` に改名した（しきい値の数字を値の名前に出さないため）。
 * dev-api の保存済みの値もすべて付け替え済みなので、旧名は受け付けない。
 */
export type MCSCutoffVersion = 'v1' | 'v0' | string;

/** 11 変数の外れ値該当日数（透明性のため返却される） */
export interface MCSOutlierCounts {
  x2?: number; x3?: number; x4?: number; x6?: number; x8?: number;
  x10?: number; x11?: number; x13?: number; x14?: number; x17?: number; x19?: number;
}

/**
 * 月次 MCS（GET /v1/employee/mcs?year_month=YYYY-MM）レスポンス。
 * Phase B（仕様 0422 §6）で完全置換された新スキーマ。
 *
 * 旧スキーマ（日次 score 時系列）からの破壊的変更:
 * - `mcs_score` は 0.0〜1.0（旧 0〜100 ではない）
 * - `score` / `confidence` / `weeklyMessage` / `last30Days` 等は廃止
 * - 該当月のデータがない場合は `available=false`
 */
export interface MCSResponse {
  available: boolean;
  /** 対象月（YYYY-MM）。未指定時は直前に終了した暦月 */
  year_month?: string | null;
  window_start?: string | null;
  window_end?: string | null;
  /** 標準化得点 */
  z?: number | null;
  /** **0.0〜1.0** の論文版 MCS 値（available=false なら null） */
  mcs_score?: number | null;
  /** mcs_score >= cutoff で true */
  risk_flag?: boolean | null;
  cutoff_version?: MCSCutoffVersion | null;
  mode_applied?: MCSModeApplied | null;
  /** 標準量 28 日に対する利用可能データ量の比 (0.0〜1.0) */
  data_volume_flag?: number | null;
  outlier_counts?: MCSOutlierCounts | null;
  /** 算出時の特記事項（参考値モード等の注釈） */
  remarks?: string | null;
  /** LLM 生成の月次コメント */
  monthly_comment?: string | null;

  // --- 表示到達までの進捗（BE 2026-09-11 / 課題 #61）---
  // #44 の確定により判定は録音日数ではなく録音量で行う。
  // FE 側で日数へ逆算しないこと（過去にそれが不整合の原因になった）。
  /** 28 日窓の解析 segment 件数 */
  segment_count?: number | null;
  /** 28 日窓の音声件数 */
  voice_count?: number | null;
  /** 表示までに必要な残り segment 件数。0 なら条件を満たしている */
  segments_to_display?: number | null;
  /** 表示までに必要な残り音声件数。0 なら条件を満たしている */
  recordings_to_display?: number | null;
  /**
   * 表示までの目安秒数。判定は OR のため **先に到達する側**で BE が見積もる
   * （本人の 1 録音あたり平均 segment 数から録音経路の所要も算出し、短い方を返す）。
   * 0 なら条件を満たしている。FE はこの値をそのまま使い、しきい値の解釈は持たない。
   */
  estimated_seconds_to_display?: number | null;
}

/** 日次レポート履歴 1 件（GET /v1/daily-reports/history の data 要素） */
export interface DailyReportHistoryItem {
  report_date: string;
  prompt_version?: string;
  model_version?: string;
  engine_version?: string | null;
  content?: Record<string, unknown>;
}

/**
 * 日次レポート履歴一覧レスポンス。
 * BE は現状 camelCase（`nextCursor` / `hasMore`）で返す。snake_case フィールドは後方互換のために残している
 */
export interface DailyReportListResponse {
  data: DailyReportHistoryItem[];
  nextCursor?: string | null;
  next_cursor?: string | null;
  hasMore?: boolean;
  has_more?: boolean;
}

function parseTrendString(raw: string): Trend[] {
  const items: Trend[] = [];
  const regex = /([^\s↑↓→]+)\s*([↑↓→])/g;
  let m: RegExpExecArray | null;
  while ((m = regex.exec(raw)) !== null) {
    items.push({ label: m[1], direction: m[2] as Trend['direction'] });
  }
  return items;
}

/** content_json から domains が読めれば新スキーマ判定 */
function isNewSchemaContent(c: Record<string, unknown>): boolean {
  return !!c && typeof c === 'object' && c.domains != null && typeof c.domains === 'object';
}

/**
 * 日次レポートの履歴の 1 件を、ダッシュボードと同じ形（DailyReport）にする（課題 #156）。
 * 過去の日の結果を、今日の結果と同じ見え方で出すため
 */
export function dailyReportFromHistoryItem(item: DailyReportHistoryItem): DailyReport | null {
  return normalizeTodayReport({ report_date: item.report_date, report: { content: item.content ?? {} } } as DailyReportTodayResponse);
}

function normalizeTodayReport(res: DailyReportTodayResponse): DailyReport | null {
  if (!res.report) return null;
  const c = res.report.content ?? {};
  const date = res.report_date;
  const comment = typeof c.comment === 'string' ? c.comment : '';
  const lastRecordedAt = typeof c.last_recorded_at === 'string' ? c.last_recorded_at : null;

  if (isNewSchemaContent(c)) {
    return {
      date,
      lastRecordedAt,
      schema: 'new',
      comment,
      domains: c.domains as DailyReportDomains,
      calm: (c.calm as DailyReportCalm) ?? undefined,
      anxietyClusters: (c.anxiety_clusters as DailyReportAnxietyClusters) ?? undefined,
      dayOverDay: (c.day_over_day as DailyReportDayOverDay) ?? undefined,
      coldStart: (c.cold_start as DailyReportColdStart) ?? undefined,
      patternANotification:
        (c.pattern_a_notification as DailyReportPatternANotification) ?? null,
      voiceCount: typeof c.voice_count === 'number' ? c.voice_count : undefined,
      mcsProgress: toDailyMcsProgress(c.mcs_progress),
    };
  }

  // 旧スキーマ（legacy）
  const rawTrends = c.trends;
  let trends: Trend[] | undefined;
  if (Array.isArray(rawTrends)) {
    trends = rawTrends
      .map((item) => {
        if (!item || typeof item !== 'object') return null;
        const row = item as Record<string, unknown>;
        const label = typeof row.label === 'string' ? row.label : null;
        const directionRaw = row.direction;
        const direction = directionRaw === '↑' || directionRaw === '↓' || directionRaw === '→'
          ? directionRaw
          : null;
        if (!label || !direction) return null;
        return { label, direction } as Trend;
      })
      .filter((item): item is Trend => item != null);
  } else if (typeof rawTrends === 'string' && rawTrends.trim() !== '') {
    trends = parseTrendString(rawTrends);
  }
  return {
    date,
    lastRecordedAt,
    schema: 'legacy',
    mcsProgress: toDailyMcsProgress(c.mcs_progress),
    comment,
    condition_emoji: (c.condition_emoji as string) ?? (c.condition as string) ?? null,
    condition: (c.condition as string) ?? null,
    trends,
  };
}

// ============================================================================
// デイリーコンディション v1.1（2026-07-02 DCS デイリー感情パラメータ×表示ロジック）
// ============================================================================
//
// 仕様書: feature/20260702/2026-07-02_確定仕様書_v1.1.html
// mockup: feature/20260702/2026-07-02_デザインイメージ_v1.1.html
// BE 回答書: feature/20260702/BE回答_FE確認依頼3件_確定版_2026-07-02.md
//
// D6 堅持: 従業員には内部コード名（good/fair/mild/tired）・p 値・MCS 数値を
// 返さず、表示専用フィールド condition_display のみを返す。

/** condition_display.tone — バッジ配色トーン（赤禁止・§3a 準拠） */
export type ConditionDisplayTone = 'green' | 'blue' | 'yellow' | 'orange';

/** バッジ表示専用フィールド (Q1 案 B 確定)。hold 時は null で返る */
export interface ConditionDisplay {
  label: string;
  tone: ConditionDisplayTone;
}

/** 疲労度ゾーン（3ゾーン: 60以上=high / 40-59=normal / 40未満=low） */
export type FatigueZone = 'high' | 'normal' | 'low';

/** 前日比トレンド（数値差は絶対に返さない、方向のみ） */
export type FatigueTrend = 'up' | 'flat' | 'down';

/**
 * MCS 精度モード。
 *
 * BE の現行経路は **解析 segment 件数ベース**（2026-08-31 / BE PR #21・#22 で確定）:
 * - `standard`        : segment 500 以上 — フル精度表示（注記なし）
 * - `short_reference` : segment 300 以上 — バッジ + comment + 参考値注記
 * - `early_reference` : segment 300 未満 かつ **音声 14 件以上** — バッジ + comment + 早期参考値注記
 * - `hold`            : それ未満 — 「データ収集中」バナーのみ（バッジ・comment は出さない）
 *
 * **segment 経路で使う音声件数のしきい値は 14 のみ**。
 * 旧はしご（音声件数 28 / 14 / 7 の `determine_mode_by_voice_count()`）は BE に後方互換で
 * 残っているが現行経路では通らない。コードだけ見ると 28/14/7 が現役に見えるので注意
 * （この取り違えが 2026-08 の hold 固定不具合の原因だった）。
 *
 * FE は値・意味とも変わらないため分岐は改修不要。切り替わるタイミングだけが変わる。
 */
export type McsMode = 'hold' | 'early_reference' | 'short_reference' | 'standard';

/** データ品質軸（Phase 1 は "ok" 固定、"hold" は v1.1 で誤記削除） */
export type DataQuality = 'ok' | 'insufficient';

/**
 * デイリーコンディション API レスポンス（従業員ロール向け）。
 * v1.1 §7 API サンプル準拠。
 *
 * BE 内部フィールド (condition_band / mcs_p / condition_score / risk_flag /
 * cutoff_version) は含まれない。
 */
export interface DailyConditionResponse {
  date: string;
  user_id?: string;

  // F1 疲労度スコア (Tension)
  fatigue_score: number;
  fatigue_zone: FatigueZone;
  fatigue_trend: FatigueTrend;
  fatigue_baseline_days: number;

  // F1-MCS コンディション傾向
  /** 200-250 字の LLM 生成メッセージ。hold でも生成されるので表示可否は FE 判断 */
  comment: string;
  /** バッジ表示用。hold 時は null */
  condition_display: ConditionDisplay | null;

  // F2 ボイスギャップ
  voice_gap_message: string | null;
  voice_gap_streak_days?: number;

  // メタ
  session_count_today?: number;
  data_quality: DataQuality;
  mcs_mode: McsMode;
}

export const employeeApi = {
  /** 今日のまとめ（未作成なら report は null）と、次にまとめを作り始める時刻の目安（#174） */
  getTodayReport: async (
    token: string | null
  ): Promise<{ report: DailyReport | null; nextGenerationAt: string | null }> => {
    const res = await api.get<DailyReportTodayResponse>('/v1/daily-reports/today', token);
    return { report: normalizeTodayReport(res), nextGenerationAt: res.next_generation_at ?? null };
  },

  /**
   * デイリーコンディション取得（v1.1 / 2026-07-02 DCS 対応）。
   * 仕様書 v1.1 §7: GET /api/v1/daily-report/{user_id}/{date}
   *
   * 従業員は自分自身の user_id (JWT sub) を渡す想定。date は YYYY-MM-DD。
   * dev-api への BE 実装反映後に実 endpoint 動作を再確認する（課題管理表 #44）。
   */
  getDailyCondition: (userId: string, date: string, token: string | null) =>
    api.get<DailyConditionResponse>(
      `/v1/daily-report/${encodeURIComponent(userId)}/${encodeURIComponent(date)}`,
      token
    ),

  /**
   * 月次論文版 MCS を取得（仕様 0422 §6 / Phase B）。
   * `yearMonth` 未指定なら直前に終了した暦月（例: 5/3 にアクセスなら 4 月分）
   */
  getMcs: (token: string | null, yearMonth?: string) => {
    const sp = new URLSearchParams();
    if (yearMonth) sp.set('year_month', yearMonth);
    const q = sp.toString();
    return api.get<MCSResponse>(`/v1/employee/mcs${q ? `?${q}` : ''}`, token);
  },

  getReportHistory: (
    token: string | null,
    params?: { limit?: number; cursor?: string | null; from?: string | null; to?: string | null }
  ) => {
    const sp = new URLSearchParams();
    if (params?.limit != null) sp.set('limit', String(params.limit));
    if (params?.cursor != null) sp.set('cursor', params.cursor);
    if (params?.from != null) sp.set('from', params.from);
    if (params?.to != null) sp.set('to', params.to);
    const q = sp.toString();
    return api.get<DailyReportListResponse>(
      `/v1/daily-reports/history${q ? `?${q}` : ''}`,
      token
    );
  },

  /**
   * 指定した日の日次レポート（課題 #156）。履歴の API をその 1 日に絞って使う（本人の分しか返らない）。
   * その日のレポートが無ければ null
   */
  getReportForDate: async (token: string | null, date: string) => {
    const res = await employeeApi.getReportHistory(token, { from: date, to: date, limit: 1 });
    return (res.data ?? []).find((item) => item.report_date === date) ?? null;
  },

  /**
   * 自分の週次レポート（新しい週から。課題 #59 段階 4 / BE PR #138）。
   * 本文が空の週は返らない。cursor は YYYY-MM-DD（その週の月曜より前の週を返す）。
   */
  getWeeklyReports: (token: string | null, params?: { limit?: number; cursor?: string | null }) => {
    const sp = new URLSearchParams();
    if (params?.limit != null) sp.set('limit', String(params.limit));
    if (params?.cursor != null) sp.set('cursor', params.cursor);
    const q = sp.toString();
    return api.get<EmployeeWeeklyReportListResponse>(`/v1/employee/weekly-reports${q ? `?${q}` : ''}`, token);
  },
};

/** 本人の週次レポート 1 件（月〜日の 1 週分） */
export interface EmployeeWeeklyReport {
  /** 週の月曜（YYYY-MM-DD） */
  week_start: string;
  /** 週の日曜（YYYY-MM-DD） */
  week_end: string;
  /** その週の振り返り。文末に「判定ではない」旨の一文が入る（BE が検査している） */
  comment: string;
  /** その週に録音した日数。分母（/7）を付けず、前の週とも比べない（2026-09-29 高尾さん決定） */
  recording_days: number;
}

export interface EmployeeWeeklyReportListResponse {
  data: EmployeeWeeklyReport[];
  nextCursor?: string | null;
}

/** 週次レポートの生成件数（管理者の確認画面・案 B。本文とメールアドレスは含まない） */
export interface AdminWeeklyReportSummary {
  /** 対象週の月曜。週次が 1 件も無ければ null */
  week_start?: string | null;
  week_end?: string | null;
  /** 本文が生成できた件数 */
  generated: number;
  /** 本文が空の件数（本人の画面には出ない） */
  failed: number;
  /** 使ったプロンプトの版ごとの件数。'default' は未登録時の既定文 */
  prompt_versions?: { prompt_version?: string | null; count: number }[];
}

/**
 * 週次レポートの確認（課題 #59・2026-09-29 高尾さん決定の案 B）。
 * 件数とプロンプトの版だけを使う。本文とメールアドレスを返す GET /v1/admin/weekly-reports は
 * 画面から使わない（本人への説明の範囲と合わないため。扱いは #136 で企業導入の前に決める）。
 * 拠点管理者には、BE が閲覧範囲の利用者の分だけを数えて返す。
 */
export const adminWeeklyReportsApi = {
  summary: (token: string | null, weekStart?: string | null) => {
    const q = weekStart ? `?${new URLSearchParams({ week_start: weekStart }).toString()}` : '';
    return api.get<AdminWeeklyReportSummary>(`/v1/admin/weekly-reports/summary${q}`, token);
  },
};

// --- 音声記録一覧・詳細（GET /v1/voice-records、GET /v1/voice-records/:id）---

/** 解析結果（DB 保存形式。一覧・詳細の analysis_json でそのまま利用可能） */
export interface VoiceRecordAnalysisJson {
  summary?: Record<string, unknown>;
  segments?: unknown[];
}

/** 音声記録 1 件（一覧要素・詳細の共通形。analysis_json は一覧・詳細どちらにも含まれる） */
export interface VoiceRecord {
  id: string;
  report_date?: string;
  file_key?: string;
  duration_seconds?: number;
  prompt_version?: string;
  model_version?: string;
  created_at?: string;
  recorded_at?: string;
  /** DB に保存されている解析結果。summary / segments をそのまま利用可能 */
  analysis_json?: VoiceRecordAnalysisJson | null;
  /**
   * 音声認識テキスト（本人のみ閲覧可）。他ユーザー・管理者・産業医からは `/v1/voice-records/{id}` が 404 を返すため取得不可。
   * 録音日 (`recorded_at`) から 6 か月経過するとバックグラウンドワーカーにより自動で NULL 化される
   */
  transcript_text?: string | null;
  [key: string]: unknown;
}

/**
 * GET /v1/voice-records 一覧レスポンス。
 * BE は現状 camelCase（`nextCursor` / `hasMore`）で返す。snake_case フィールドは後方互換のために残している
 */
export interface VoiceRecordListResponse {
  data: VoiceRecord[];
  next_cursor?: string | null;
  nextCursor?: string | null;
  has_more?: boolean;
  hasMore?: boolean;
}

export const voiceRecordsApi = {
  getList: (
    token: string | null,
    params?: { limit?: number; cursor?: string | null; from?: string | null; to?: string | null }
  ) => {
    const sp = new URLSearchParams();
    if (params?.limit != null) sp.set('limit', String(params.limit));
    if (params?.cursor != null) sp.set('cursor', params.cursor ?? '');
    if (params?.from != null) sp.set('from', params.from);
    if (params?.to != null) sp.set('to', params.to);
    const q = sp.toString();
    return api.get<VoiceRecordListResponse>(
      `/v1/voice-records${q ? `?${q}` : ''}`,
      token
    );
  },

  getById: (id: string, token: string | null) =>
    api.get<VoiceRecord>(`/v1/voice-records/${id}`, token),
};

// --- 管理者向け API（ADM-S05 ダッシュボード用）---

export interface DashboardUsersSummary {
  total: number;
  active: number;
  inactive: number;
}

export interface DashboardJobsSummary {
  total: number;
  succeeded: number;
  failed: number;
  total_duration_seconds: number;
  average_duration_seconds: number;
}

export interface DashboardMcsSummary {
  average_score: number | null;
  min_score: number | null;
  max_score: number | null;
  sample_count: number;
  /**
   * BE 側 k-匿名化サプレッション（2026-05-30 追加）。
   * sample_count < threshold の場合 true で、各 *_score は null に置換される。
   * FE は suppressed=true のとき「人数が少ないため非表示」表示を行う
   */
  suppressed?: boolean;
  suppression_reason?: string | null;
  threshold?: number;
}

export interface DashboardReportsSummary {
  total_reports: number;
  active_users_with_reports: number;
}

export interface OrganizationDashboardResponse {
  from_date: string;
  to_date: string;
  users: DashboardUsersSummary;
  jobs: DashboardJobsSummary;
  mcs: DashboardMcsSummary;
  daily_reports: DashboardReportsSummary;
}

export interface DepartmentSummary {
  id: string;
  name: string;
  status: string;
  user_count: number;
}

export interface OrganizationViewResponse {
  total_users: number;
  active_users: number;
  inactive_users: number;
  k_anonymity: number;
  hidden_departments: number;
  departments: DepartmentSummary[];
}

export const adminApi = {
  getOrganization: (token: string | null) =>
    api.get<OrganizationViewResponse>('/v1/organization', token),

  getDashboard: (token: string | null) =>
    api.get<OrganizationDashboardResponse>('/v1/organization/dashboard', token),
};

// --- ユーザー管理（ADM-F08/F09/F10/F11）---

/** 目的ごとの同意状態（課題 #57 / BE の ConsentPurposeStatus） */
export interface ConsentPurposeStatus {
  /**
   * その目的に必要な同意が全て granted なら true。
   * **必要な同意が 1 つも定義されていない（published な同意文書が無い）場合は null。**
   * 「全員可」と「そもそも同意を取っていない」を取り違えないための区別。
   */
  granted?: boolean | null;
  /** 不足している同意 */
  missing?: { purpose?: string; data_type?: string; label_ja?: string }[];
}

export interface AdminUserResponse {
  id: string;
  email: string;
  /** 氏名（任意。課題 #51・#94） */
  display_name?: string | null;
  role: string;
  status: string;
  department_id?: string | null;
  /** 目的ごとの同意状態。キーは personal_view / healthcare_detail */
  consents?: Record<string, ConsentPurposeStatus>;
}

export interface UserInviteRequest {
  email: string;
  /** 氏名（任意・100 字まで）。受諾して利用者が作られるときに引き継がれる */
  display_name?: string;
  role?: string | null;
  expires_in_days?: number | null;
}

/**
 * `POST /v1/admin/users` のレスポンス。
 *
 * `invite_token` は返らないことがある（null）。返ってきた場合は受諾 URL を
 * 組み立てられるので、メールが届かない方へ別の経路で渡せる。
 */
export interface UserInviteResponse {
  expires_at: string;
  email_sent: boolean;
  invite_token?: string | null;
}

/** `POST /v1/admin/users/resend-invite` のリクエスト（課題 #54） */
export interface InviteResendRequest {
  /** 再送先。未受諾の招待が存在する必要がある */
  email: string;
  /** 新しい招待の有効期限。既定 7 日 */
  expires_in_days?: number | null;
  /**
   * 受諾 URL と招待トークンを応答に含めるか。既定 false。
   *
   * **本人がメールを確認できない場合に、管理者が直接伝えるための逃げ道**であり、
   * 送信が成功しているのに取り出せる状態を既定にはしない、という BE の設計。
   * true にした実行は監査ログに別イベントとして残る。
   */
  include_token?: boolean;
}

/** `POST /v1/admin/users/resend-invite` のレスポンス（課題 #54） */
export interface InviteResendResponse {
  email: string;
  expires_at: string;
  email_sent: boolean;
  email_error?: string | null;
  /**
   * 元の招待を無効化したか。**再送すると前のリンクは使えなくなる。**
   * 有効なリンクが複数あると、どれが生きているか分からなくなるため。
   */
  previous_invite_invalidated: boolean;
  /**
   * 受諾 URL。`include_token` が true のとき、または**メール送信に失敗した**ときに返る。
   * 招待受付の base URL が未設定の環境では null になるため、
   * 呼び出し側は invite_token から自分のオリジンで組み立てられるようにしておく。
   */
  invite_url?: string | null;
  /** 招待トークン。返却条件は invite_url と同じ */
  invite_token?: string | null;
}

export interface UserUpdateRequest {
  /** 氏名。空文字で消す。未指定なら変更しない */
  display_name?: string;
  role?: string | null;
  status?: string | null;
  department_id?: string | null;
}

/**
 * パスワードの再設定の応答。鍵は本人のメールにだけ送り、管理者には返さない（#51）。
 * 鍵を受け取った管理者が、その方としてログインできるため。
 */
export interface UserResetResponse {
  email_sent: boolean;
  expires_at: string;
}

export const adminUsersApi = {
  list: (token: string | null, params?: { limit?: number }) => {
    const sp = new URLSearchParams();
    if (params?.limit != null) sp.set('limit', String(params.limit));
    const q = sp.toString();
    return api.get<AdminUserResponse[]>(`/v1/admin/users${q ? `?${q}` : ''}`, token);
  },

  invite: (body: UserInviteRequest, token: string | null, idempotencyKey?: string) => {
    const payload: Record<string, unknown> = { email: body.email.trim() };
    // 氏名（任意。#94 / #51）。受諾して利用者が作られるときに引き継がれる
    const name = body.display_name?.trim();
    if (name) payload.display_name = name;
    if (body.role != null && String(body.role).trim() !== '') {
      payload.role = body.role;
    }
    const d = body.expires_in_days;
    if (typeof d === 'number' && Number.isFinite(d)) {
      const n = Math.floor(d);
      if (n >= 1 && n <= 30) payload.expires_in_days = n;
    }
    const key = idempotencyKey ?? crypto.randomUUID();
    return api.post<UserInviteResponse>('/v1/admin/users', payload, token, key);
  },

  /**
   * 未受諾の招待を再送する（課題 #54）。
   *
   * 有効な招待が残っている間は通常の招待が 409 になるため、送り直す手段として使う。
   * **期限切れには使わない**（404 が返る。その場合は通常の招待で送り直せる）。
   * 再送すると**元の招待は無効になる**ので、前に渡したリンクは使えなくなる。
   */
  resendInvite: (body: InviteResendRequest, token: string | null, idempotencyKey?: string) => {
    const payload: Record<string, unknown> = { email: body.email.trim() };
    const d = body.expires_in_days;
    if (typeof d === 'number' && Number.isFinite(d)) {
      const n = Math.floor(d);
      if (n >= 1 && n <= 30) payload.expires_in_days = n;
    }
    // 既定で送らない。監査ログに別イベントとして残るため、意図したときだけ true にする
    if (body.include_token === true) payload.include_token = true;
    const key = idempotencyKey ?? crypto.randomUUID();
    return api.post<InviteResendResponse>('/v1/admin/users/resend-invite', payload, token, key);
  },

  update: (userId: string, body: UserUpdateRequest, token: string | null) =>
    api.put<AdminUserResponse>(`/v1/admin/users/${encodeURIComponent(userId)}`, body, token),

  resetPassword: (userId: string, token: string | null, idempotencyKey?: string) =>
    api.post<UserResetResponse>(
      `/v1/admin/users/${encodeURIComponent(userId)}/reset`,
      undefined,
      token,
      idempotencyKey ?? crypto.randomUUID()
    ),

  /**
   * ユーザーを完全削除する（BE 依頼中、`docs/BACKEND_admin_user_delete_export_request.md` 参照）。
   * BE 未対応時は 404 / 405 / 501 が返る想定 — 呼び出し側で捕捉する
   */
  delete: (userId: string, token: string | null, idempotencyKey?: string) => {
    const key = idempotencyKey ?? crypto.randomUUID();
    return api.delete<void>(`/v1/admin/users/${encodeURIComponent(userId)}`, token, key);
  },

  /**
   * ユーザーのデータを CSV でエクスポート（BE 依頼中）。
   * レスポンスは text/csv（UTF-8 BOM 付きを推奨）。email / スコア / 解析結果に加え、
   * 管理者向け特別対応として transcript_text も含める方針（要 BE + 法務調整）
   */
  /**
   * ユーザー一覧（名簿）を CSV でエクスポートする（BE 2026-09-02 新設 / openapi 116 操作）。
   *
   * `GET /v1/admin/users/export?format=csv`
   * 列: email, role, status, department_name, created_at, invite_accepted_at, last_login_at
   *
   * **1 名分の `exportCsv()` とは用途が異なる**。あちらは開示請求用で、
   * 音声記録・文字起こしまで含めて 1 名につき数百行を出す。こちらは名簿一覧で、
   * 文字起こしは含まない。部署は ID ではなく名称、UTF-8 BOM 付きで Excel から直接開ける。
   * 個人データの持ち出しのため BE 側で監査ログに記録される。
   */
  exportRosterCsv: async (token: string | null): Promise<Blob> => {
    const url = `${API_BASE.replace(/\/$/, '')}/v1/admin/users/export?format=csv`;
    const headers: Record<string, string> = { Accept: 'text/csv' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await fetch(url, { method: 'GET', headers, credentials: 'same-origin' });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new RequestError(
        extractApiErrorMessage(data, res.statusText || 'export_failed'),
        res.status,
        data,
        extractErrorCode(data),
        extractErrorDetails(data)
      );
    }
    return res.blob();
  },

  exportCsv: async (userId: string, token: string | null): Promise<Blob> => {
    const url = `${API_BASE.replace(/\/$/, '')}/v1/admin/users/${encodeURIComponent(userId)}/export?format=csv`;
    const headers: Record<string, string> = { Accept: 'text/csv' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await fetch(url, { method: 'GET', headers, credentials: 'same-origin' });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new RequestError(
        extractApiErrorMessage(data, res.statusText || 'export_failed'),
        res.status,
        data,
        extractErrorCode(data),
        extractErrorDetails(data)
      );
    }
    return res.blob();
  },
};

// --- プロンプト管理（ADM-S01 / ADM-S02）---

export interface PromptResponse {
  id: string;
  title: string;
  version: string;
  effective_from: string;
  status: string;
  /**
   * どの業務ワークフローで利用されるプロンプトかを示す識別子。
   * 2026-05-30: BE が対応。値が無い旧データに対しては FE 側で title 命名規則による fallback を維持する
   */
  workflow_type?: string | null;
}

export interface PromptDetailResponse extends PromptResponse {
  content: string;
  parent_id?: string | null;
}

export interface PromptCreateRequest {
  title: string;
  content: string;
  reason: string;
  version?: string | null;
  effective_from?: string | null;
  /** 2026-05-30〜: BE が対応。未選択時は省略可 */
  workflow_type?: string | null;
}

export interface PromptUpdateRequest {
  reason: string;
  title?: string | null;
  content?: string | null;
  version?: string | null;
  effective_from?: string | null;
  workflow_type?: string | null;
}

export interface PromptHistoryResponse {
  items: PromptDetailResponse[];
}

/**
 * Markdown バリデーションエラー（POST/PUT /v1/admin/consent-documents）の
 * details.errors[] 要素（2026-05-30 BE 仕様変更）。
 * `rule` は "禁止語" / "REG-01" / "空文書" / "見出し書式" 等
 */
export interface MarkdownValidationError {
  line: number;
  column: number;
  rule: string;
  message: string;
  snippet?: string | null;
}

export interface MarkdownValidationErrorDetail {
  errors: MarkdownValidationError[];
  /** 旧 patterns（後方互換のため BE は残している） */
  patterns?: unknown[];
}

/** healthcare/person 取得時の同意不足エラー（CONSENT_REQUIRED）の details */
export interface HealthcareConsentRequiredDetail {
  missing_consents: Array<{
    purpose: string;
    data_type: string;
    label_ja: string;
  }>;
  consent_mode: 'anonymous' | 'personal_identified' | string;
}

export const promptsApi = {
  list: (token: string | null) =>
    api.get<PromptResponse[]>('/v1/admin/prompts', token),

  get: (promptId: string, token: string | null) =>
    api.get<PromptDetailResponse>(`/v1/admin/prompts/${encodeURIComponent(promptId)}`, token),

  /**
   * 作成は Idempotency-Key を付けて送る。BE は「FE がキーを送らない」ため、この API を一時的に
   * キーの検査から外している（SKIP_IDEMPOTENCY_PATHS）。FE が送るようになれば、外す必要が無くなる
   */
  create: (body: PromptCreateRequest, token: string | null, idempotencyKey?: string) =>
    api.post<PromptDetailResponse>('/v1/admin/prompts', body, token, idempotencyKey ?? crypto.randomUUID()),

  update: (promptId: string, body: PromptUpdateRequest, token: string | null) =>
    api.put<PromptDetailResponse>(`/v1/admin/prompts/${encodeURIComponent(promptId)}`, body, token),

  delete: (promptId: string, token: string | null) =>
    api.delete<unknown>(`/v1/admin/prompts/${encodeURIComponent(promptId)}`, token),

  getHistory: (promptId: string, token: string | null) =>
    api.get<PromptHistoryResponse>(`/v1/admin/prompts/${encodeURIComponent(promptId)}/history`, token),

  /**
   * プロンプトを試し打ちして生成文を返す（課題 #58）。
   *
   * **保存しない。** 編集中の本文をそのまま送るだけで、DB には書かれない。
   * LLM を 1 回呼ぶため、費用面から BE 側で 1 時間あたりの回数制限がある（429）。
   */
  preview: (body: PromptPreviewRequest, token: string | null) =>
    api.post<PromptPreviewResponse>('/v1/admin/prompts/preview', body, token),
};

/** POST /v1/admin/prompts/preview のリクエスト（課題 #58） */
export interface PromptPreviewRequest {
  /** 試し打ちするプロンプト本文（保存されない） */
  content: string;
  /**
   * 入力データの種類。
   * `synthetic`（既定）は固定の合成データなので、文面を変えたときの差分が
   * 文面の効果だけになる。`recent_anonymized` も受け付けるが、2026-10-31 の BE PR #198 から
   * 直近の録音の値は使わず、同じ固定の合成データで試す（応答の `sample_kind` は `synthetic`）。
   * 直近の録音の値は、プロンプトの書き方しだいで管理者がそのまま読み出せたため（#51）。
   */
  sample_kind?: string;
  /** daily_report / monthly_report / health_management */
  workflow_type?: string | null;
}

/** POST /v1/admin/prompts/preview のレスポンス（課題 #58） */
export interface PromptPreviewResponse {
  /** 生成された文章。REG-01 適用後＝実際に利用者へ出る文面 */
  generated_text: string | null;
  /**
   * REG-01 の適用で文面が変化したか。
   * true なら書いたとおりには出ていない（禁止表現が置換されている）。
   */
  sanitized: boolean;
  /** 実際に使われた入力データの種類 */
  sample_kind: string;
  /** LLM 呼び出しにかかった時間 */
  elapsed_ms: number;
  /**
   * 生成後の検査結果（課題 #62）。**本番と同じ判定**。
   * これが無いと、試し打ちで良く見えた文面が本番では差し替わる。
   */
  checks?: PromptPreviewChecks | null;
  /**
   * 締めの一文をコードで文末に付け直したか（BE PR #116 以降。課題 #62）。
   * true なら、モデルが指定どおりに書かず、コードが付け直した。
   * 項目が無い（undefined）場合は、付け直しに対応する前の BE。
   */
  closing_sentence_added?: boolean;
  /**
   * 本番ならこの文面で書き直しが入るか。true なら 1 度だけ指摘して再生成され、
   * それでも落ちれば定型文になる。一度で通る文面かどうかの判断に使う。
   */
  would_retry?: boolean;
}

/** 試し打ちの生成後検査（課題 #62） */
export interface PromptPreviewChecks {
  /** すべての検査を通ったか */
  passed: boolean;
  /** 「不調の判定ではない」旨の一文が無い。この指示を文面から外すと毎回ここで落ちる */
  non_diagnostic_note_missing: boolean;
  /** 算出済みなのに「算出できなかった」と述べている */
  contradicts_mcs_availability?: boolean;
  /** MCS の数値・バンド名・グラフ等、従業員に開示してはならない内容 */
  leaks_internal_values?: string[];
  /**
   * 日次のみ（BE PR #132 以降。課題 #62）: 当日以外と比べる言い方（「最近」「続いている」など）。
   * 項目が無ければ、日次の生成後の検査に対応する前の BE。
   */
  comparison_expressions?: string[];
  /** 医療機器としての認証を騙る主張・病名（REG-01） */
  prohibited_claims?: string[];
  /**
   * 日次・週次（課題 #62 / BE PR #170）: 指定した文字列のうち、扱いが「有効」のまとまりで見つかったもの
   * （`まとまり:文字列`）。本番ではこれで書き直しになる（passed も false）
   */
  phrase_rules?: string[];
  /**
   * 日次・週次（課題 #62 / BE PR #170）: 扱いが「記録だけ」のまとまりで見つかったもの（`まとまり:文字列`）。
   * 本番の文面は変えず、件数だけを数える。passed には影響しない（画面では合否と分けて参考に出す）
   */
  phrase_records?: string[];
}

// --- 健康経営レポート（ADM-S03 / ADM-S04）---

export interface AdminReportResponse {
  id: string;
  status: string;
  output_format: string;
  period_from: string;
  period_to: string;
  created_at: string;
  completed_at: string | null;
}

export interface AdminReportListResponse {
  data: AdminReportResponse[];
  nextCursor?: string | null;
  hasMore?: boolean;
}

export interface AdminReportCreateRequest {
  period_from: string;
  period_to: string;
  output_format?: string;
}

export interface AdminReportDownloadResponse {
  download_url: string;
  expires_in: number;
}

export const reportsApi = {
  list: (token: string | null, params?: { limit?: number; cursor?: string | null }) => {
    const sp = new URLSearchParams();
    if (params?.limit != null) sp.set('limit', String(params.limit));
    if (params?.cursor != null) sp.set('cursor', params.cursor);
    const q = sp.toString();
    return api.get<AdminReportListResponse>(`/v1/admin/reports${q ? `?${q}` : ''}`, token);
  },

  /** 作成は Idempotency-Key を付けて送る（プロンプトの作成と同じ理由。BE の一時的な除外を外せるように） */
  create: (body: AdminReportCreateRequest, token: string | null, idempotencyKey?: string) =>
    api.post<AdminReportResponse>('/v1/admin/reports', body, token, idempotencyKey ?? crypto.randomUUID()),

  getDownloadUrl: (reportId: string, token: string | null) =>
    api.get<AdminReportDownloadResponse>(`/v1/admin/reports/${encodeURIComponent(reportId)}/download-url`, token),
};

// --- 管理者設定（組織別 system_settings / APIキー等）---

export interface AdminSettingItem {
  key: string;
  value?: string | null;
  encrypted?: boolean;
  /** 2026-06-09 DCS 様対応: 値が明示的に設定されているか（false = BE デフォルト適用中） */
  is_set?: boolean;
  /** 2026-06-09: BE が GET /v1/admin/settings 時に meta を同梱するケースに対応 */
  meta?: AdminSettingMetaExtended;
}

/** 拡張版 meta (options / value_type 等を含む 2026-06-09 〜) */
export interface AdminSettingMetaExtended {
  value_type?: 'str' | 'int' | 'float' | 'bool' | null;
  recommended?: number | string | null;
  min?: number | null;
  max?: number | null;
  unit?: string | null;
  options?: string[] | null;
  description_ja?: string | null;
  is_secret?: boolean;
  default?: number | string | null;
}

export interface AdminSettingsGetResponse {
  items: AdminSettingItem[];
}

export interface AdminSettingsPutRequest {
  items: Array<{ key: string; value: string; encrypted?: boolean }>;
}

export const adminSettingsApi = {
  get: (token: string | null) =>
    api.get<AdminSettingsGetResponse>('/v1/admin/settings', token),

  update: (body: AdminSettingsPutRequest, token: string | null) =>
    api.put<AdminSettingsGetResponse>('/v1/admin/settings', body, token),

  /**
   * GET /v1/admin/settings/meta — 各設定キーの推奨値・min/max/unit/description を取得（2026-05-30 追加）。
   * UI のプレースホルダー・min/max バリデーション・ヘルプ表示に活用
   */
  getMeta: (token: string | null) =>
    api.get<AdminSettingsMetaResponse>('/v1/admin/settings/meta', token),
};

/** GET /v1/admin/settings/meta の各キーのメタ情報 */
export interface AdminSettingMeta {
  recommended?: number | string | null;
  min?: number | null;
  max?: number | null;
  unit?: string | null;
  description_ja?: string | null;
  is_secret?: boolean;
  default?: number | string | null;
}

export interface AdminSettingsMetaItem {
  key: string;
  meta: AdminSettingMeta;
}

export interface AdminSettingsMetaResponse {
  items: AdminSettingsMetaItem[];
}

// --- 監査ログ（2026-06-09 DCS 様対応 / F-2 + F-3）---

/** event_type の代表値（DCS 様向けの破棄証跡 3 種を含む） */
export type AuditEventType =
  | 'admin.user.deleted'
  | 'admin.user.exported'
  | 'consent.granted'
  | 'consent.revoked'
  | 'data_request.approved'
  | 'data_request.rejected'
  | 'prompt.deleted'
  | 'settings.viewed'
  | 'settings.updated'
  | 'tenant.provisioned'
  | 'voice_file.deleted'
  | 'transcript.nulled'
  | 'transcript.never_stored'
  | (string & {});

export interface AuditLogEntry {
  id: string;
  created_at: string;
  event_type: AuditEventType;
  actor_user_id?: string | null;
  actor_role?: string | null;
  resource_type?: string | null;
  resource_id?: string | null;
  ip_address?: string | null;
  user_agent?: string | null;
  event_metadata?: Record<string, unknown> | null;
}

/**
 * 破棄証跡の注記（課題 No.61 / BE 2026-08-26 追加）。
 * 破棄証跡イベントを含み得る取得時のみ返る（無関係な監査ログ取得では null）。
 * `recordingStartedOn` より前の破棄には証跡が存在しない（削除機能 2026-04-14 / 証跡機能 2026-06-13 の実装差）。
 * 記録開始日は BE が返すため FE ではハードコードしない。
 */
export interface AuditLogsEvidenceNotice {
  /** 破棄証跡の記録を開始した日（YYYY-MM-DD） */
  recordingStartedOn: string;
  /** 破棄証跡として扱うイベント種別 */
  eventTypes: string[];
  /** 表示用の日本語注記（そのまま表示してよい） */
  note: string;
}

/**
 * GET /v1/admin/audit-logs のレスポンス。
 * BE 契約（openapi `AuditLogListResponse`）は `data` / `hasMore` / `nextCursor`。
 * `items` / `total` / `has_more` は旧 FE 実装が前提にしていた形で、後方互換のため残している
 * （実際には BE が返さないため、読み出しは `data` を優先する）。
 */
export interface AuditLogsResponse {
  data?: AuditLogEntry[];
  evidenceNotice?: AuditLogsEvidenceNotice | null;
  hasMore?: boolean;
  nextCursor?: string | null;
  /** @deprecated BE は返さない。旧実装との互換のみ */
  items?: AuditLogEntry[];
  /** @deprecated BE は返さない */
  total?: number;
  /** @deprecated BE は `hasMore` を返す */
  has_more?: boolean;
}

export interface AuditLogsListParams {
  event_type?: string;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}

export interface AuditLogsExportParams {
  /** カンマ区切りで複数 event_type 指定可能（OR 条件） */
  event_type?: string;
  from?: string;
  to?: string;
  format?: 'csv' | 'json';
}

function buildQuery(params: Record<string, string | number | undefined | null>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    usp.append(k, String(v));
  }
  const q = usp.toString();
  return q ? `?${q}` : '';
}

export const adminAuditLogsApi = {
  list: (params: AuditLogsListParams, token: string | null) =>
    api.get<AuditLogsResponse>(
      `/v1/admin/audit-logs${buildQuery({ ...params })}`,
      token
    ),

  /**
   * GET /v1/admin/audit-logs/export — CSV/JSON ダウンロード。
   * 認証は Authorization ヘッダ必須のため fetch + blob で取得し、ブラウザ標準のダウンロードに渡す。
   */
  export: async (params: AuditLogsExportParams, token: string | null): Promise<Blob> => {
    const qs = buildQuery({ ...params });
    const res = await fetch(`/api/v1/admin/audit-logs/export${qs}`, {
      method: 'GET',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      throw new RequestError(`audit logs export failed (${res.status})`, res.status);
    }
    return await res.blob();
  },
};

// --- 部署マスタ（2026-05-30 追加 / 課題管理表 #4）---

export interface DepartmentResponse {
  id: string;
  name: string;
  code: string | null;
  status: string;
}

export interface DepartmentListResponse {
  data: DepartmentResponse[];
}

export interface DepartmentCreateRequest {
  name: string;
  code?: string | null;
}

export interface DepartmentUpdateRequest {
  name?: string | null;
  code?: string | null;
  status?: string | null;
}

// --- CSV による一括登録（課題 #94 / #51）---

/** 確認の 1 行。result: ready（登録する）/ skipped（飛ばす）/ error（登録できない） */
export interface BulkImportRow {
  /** CSV 上の行番号。見出しが 1 行目（Excel で見える行番号と同じ） */
  row: number;
  email: string;
  display_name?: string | null;
  role: string;
  department_name?: string | null;
  result: string;
  /** 飛ばす・登録できない理由。BE がそのまま表示できる文で返す */
  reasons?: string[];
}

/** 取り込みの 1 行。result: invited（招待した）/ skipped / error */
export interface BulkImportResultRow extends BulkImportRow {
  /** 招待メールを送れたか（招待した行のみ） */
  email_sent?: boolean | null;
  email_error?: string | null;
  /** 招待メールを送れなかった行だけ返る。リンクを別の方法で本人に渡すときに使う */
  invite_token?: string | null;
}

export interface BulkImportWarning {
  row: number;
  kind: string;
  message: string;
}

export interface SimilarDepartment {
  name: string;
  department_id: string;
  /** 例:「漢数字と数字の違いのみ」 */
  reason: string;
}

/** 取り込むと新しく作られる部署。similar_existing が空でなければ、表記ゆれの可能性が高い */
export interface NewDepartment {
  name: string;
  row_count: number;
  similar_existing?: SimilarDepartment[];
  /**
   * CSV の中のほかの新しい部署のうち、似ているもの（課題 #94。2026-09-30 高尾さん了承。BE PR #152）。
   * 「第一営業部」と「第1営業部」が両方とも新規のとき、既存の部署とだけ比べると注意が出ないため。
   * この項目を返さない BE では undefined（注意を出さない）
   */
  similar_new?: SimilarNewDepartment[];
}

/** CSV の中の、似ている新しい部署（まだ ID が無いので名前だけ） */
export interface SimilarNewDepartment {
  name: string;
  /** 例:「漢数字と数字の違いのみ」 */
  reason: string;
}

export interface ExistingDepartment {
  name: string;
  department_id: string;
  row_count: number;
}

export interface BulkImportPreviewResponse {
  total_rows: number;
  ready_rows: number;
  skipped_rows: number;
  error_rows: number;
  rows: BulkImportRow[];
  existing_departments: ExistingDepartment[];
  new_departments: NewDepartment[];
  warnings: BulkImportWarning[];
}

export interface BulkImportResponse {
  total_rows: number;
  invited: number;
  skipped: number;
  errors: number;
  email_failed: number;
  rows: BulkImportResultRow[];
  created_departments: { name: string; department_id: string }[];
  warnings: BulkImportWarning[];
}

function csvForm(file: File): FormData {
  const form = new FormData();
  form.append('file', file);
  return form;
}

/**
 * CSV（100 行まで。列は email / display_name / role / department_name）から、まとめて招待する。
 * 確認は DB に書かない。取り込みは登録できる行だけを招待する（1 行の誤りで全体をやり直さない）。
 */
export const adminUsersBulkImportApi = {
  preview: (file: File, token: string | null) =>
    api.post<BulkImportPreviewResponse>('/v1/admin/users/bulk-import/preview', csvForm(file), token),
  import: (file: File, token: string | null, idempotencyKey?: string) =>
    api.post<BulkImportResponse>(
      '/v1/admin/users/bulk-import',
      csvForm(file),
      token,
      idempotencyKey ?? crypto.randomUUID()
    ),
};

// --- 既存の利用者の部署の一括更新（課題 #94。2026-09-30 高尾さん了承。BE PR #152 で FE の提案の形のまま反映）---

/**
 * 部署の一括更新の 1 行。確認: change（変える）/ unchanged（今と同じ部署）/ error（変えられない）。
 * 反映: updated（変えた）/ unchanged / error
 */
export interface BulkDepartmentRow {
  /** CSV 上の行番号。見出しが 1 行目（Excel で見える行番号と同じ） */
  row: number;
  email: string;
  /** 今の部署（未設定なら null）。登録されていないメールアドレスの行では null */
  current_department_name?: string | null;
  /** CSV で指定した、変更後の部署 */
  department_name?: string | null;
  result: string;
  /** 変えられない理由など。BE がそのまま表示できる文で返す */
  reasons?: string[];
}

export interface BulkDepartmentPreviewResponse {
  total_rows: number;
  change_rows: number;
  unchanged_rows: number;
  error_rows: number;
  rows: BulkDepartmentRow[];
  /** 反映すると新しく作られる部署（全社管理者のみ作れる）。似ている部署の注意は一括登録と同じ */
  new_departments: NewDepartment[];
  warnings: BulkImportWarning[];
}

export interface BulkDepartmentResponse {
  total_rows: number;
  updated: number;
  unchanged: number;
  errors: number;
  rows: BulkDepartmentRow[];
  created_departments: { name: string; department_id: string }[];
  warnings: BulkImportWarning[];
}

/**
 * CSV（列は email / department_name）で、登録済みの利用者の部署をまとめて変える。
 * 確認は DB に書かない。反映は変えられる行だけを変える（1 行の誤りで全体をやり直さない）。
 * 拠点管理者は、閲覧範囲の方を閲覧範囲の部署にだけ移せる（新しい部署は作れない）。
 * 変更は 1 名ずつ監査ログに残る。CSV は 1000 行まで、UTF-8（BOM 付き可）と Shift_JIS を受け付ける。
 * 古い BE ではルートが無く 404
 */
export const adminUsersBulkDepartmentApi = {
  preview: (file: File, token: string | null) =>
    api.post<BulkDepartmentPreviewResponse>('/v1/admin/users/bulk-department/preview', csvForm(file), token),
  apply: (file: File, token: string | null, idempotencyKey?: string) =>
    api.post<BulkDepartmentResponse>(
      '/v1/admin/users/bulk-department',
      csvForm(file),
      token,
      idempotencyKey ?? crypto.randomUUID()
    ),
};

// --- 通知先（Webhook）の管理（課題 #113）---

/**
 * 宛先の種別。Slack と Teams は送る JSON の形が違う。generic は署名付きのイベント JSON。
 * chatwork は URL の代わりにルーム ID と API トークンで投稿する（課題 #113 / BE PR #141）
 */
export type WebhookKind = 'generic' | 'slack' | 'teams' | 'chatwork';

/** 一覧の 1 件。**URL は末尾だけ**（Webhook URL はそれ自体が認証情報のため BE が伏せる） */
export interface WebhookItem {
  id: string;
  url: string;
  kind?: WebhookKind | string;
  events: string[];
  status: 'active' | 'inactive' | string;
  created_at: string;
  /** 用途・送り先チャンネル名（自由記述）。URL は末尾しか出ないため、登録を見分ける手がかりにする */
  label?: string | null;
  /** chatwork のみ: API トークンの末尾 4 文字（「…abcd」）。トークンの全体は返らない */
  credential_tail?: string | null;
}

export interface WebhookCreateRequest {
  /** generic / slack / teams では必須。chatwork では送らない（送ると 422） */
  url?: string;
  /** chatwork のみ: 投稿するルームの ID（数字） */
  chatwork_room_id?: string;
  /**
   * chatwork のみ: 通知専用アカウントの API トークン（10〜200 字）。BE が暗号化して保存し、
   * 以後は末尾しか返さない。そのアカウントが参加するすべてのルームに投稿できるため、
   * 通知専用のアカウントを通知用のルームにだけ参加させて使う
   */
  chatwork_api_token?: string;
  events: string[];
  kind: WebhookKind;
  /** 用途・送り先チャンネル名（任意・100 字まで） */
  label?: string;
  /** 登録時にテスト通知を 1 通送って届くかを確かめる（2026-09-24 CENTRIC 決定: 既定で行う） */
  verify: boolean;
}

export interface WebhookCreateResponse extends WebhookItem {
  /** generic の署名検証に使う鍵。**この応答でのみ返る**。chatwork では null */
  secret: string | null;
  /** 登録した URL の全体。この応答でのみ返る */
  full_url: string;
  /** true: 届いた / false: 届かなかった（登録はされるが無効になる）/ null: 未実施 */
  verified?: boolean | null;
  verify_error?: string | null;
}

export interface WebhookUpdateRequest {
  events?: string[];
  kind?: WebhookKind;
  /** 空文字で消す */
  label?: string;
  /** 失効させるには inactive */
  status?: 'active' | 'inactive';
}

/**
 * 全社管理者だけが使える（拠点管理者は 403）。
 * 通知先は組織全体のもので、拠点ごとに分かれていないため。
 */
export const webhooksApi = {
  /** 無効の登録も含めて返す（届かず無効になった登録を一覧で見分けられるように） */
  list: (token: string | null) => api.get<WebhookItem[]>('/v1/webhooks?include_inactive=true', token),
  create: (body: WebhookCreateRequest, token: string | null, idempotencyKey?: string) =>
    api.post<WebhookCreateResponse>('/v1/webhooks', body, token, idempotencyKey ?? crypto.randomUUID()),
  update: (webhookId: string, body: WebhookUpdateRequest, token: string | null) =>
    api.put<WebhookItem>(`/v1/webhooks/${encodeURIComponent(webhookId)}`, body, token),
  /**
   * 削除する（BE PR #123）。削除したものは一覧に出ず、変更も有効化もできない。
   * 一時的に止めるだけなら update で status を inactive にする。
   */
  remove: (webhookId: string, token: string | null) =>
    api.delete<unknown>(`/v1/webhooks/${encodeURIComponent(webhookId)}`, token),
};

// --- 管理者の閲覧範囲（課題 #100）---

/**
 * 管理者 1 人ぶんの閲覧範囲。
 * all = 全社管理者（すべて見られる）/ departments = 拠点管理者（割り当てた部署だけ）。
 */
export interface AdminScopeItem {
  user_id: string;
  admin_scope: 'all' | 'departments' | string;
  department_ids?: string[];
  department_names?: string[];
  email?: string | null;
  /** 拠点管理者なのに閲覧範囲が 1 件も無い。**何も見えない**（割り当ての消し忘れ） */
  sees_nothing?: boolean;
  /**
   * 利用者の削除ができるか（課題 #71。/me だけ。BE 357621b、2026-10-07 本番）。既定は false（止めている）。
   * false の間は DELETE /v1/admin/users/{id} が 409 USER_DELETION_SUSPENDED を返す。無い BE では FE は今の画面のままにする
   */
  user_deletion_enabled?: boolean;
}

export interface AdminScopeUpdateRequest {
  admin_scope: 'all' | 'departments';
  /** departments のときの閲覧範囲。空のまま departments にすると何も見えなくなる */
  department_ids: string[];
}

/**
 * 全社管理者だけが使える。拠点管理者が呼ぶと 403。
 * 自分自身は変更できない（全社管理者が 0 人になると誰も戻せなくなるため）。
 */
export const adminScopesApi = {
  list: (token: string | null) => api.get<AdminScopeItem[]>('/v1/admin/admin-scopes', token),
  /**
   * ログイン中の管理者自身の閲覧範囲（BE PR #124）。拠点管理者も呼べる。
   * department_ids が空のときは admin_scope で見分ける（空だから全部見える、ではない）。
   */
  me: (token: string | null) => api.get<AdminScopeItem>('/v1/admin/admin-scopes/me', token),
  update: (userId: string, body: AdminScopeUpdateRequest, token: string | null) =>
    api.put<AdminScopeItem>(`/v1/admin/admin-scopes/${encodeURIComponent(userId)}`, body, token),
};

export const adminDepartmentsApi = {
  /**
   * 部署一覧を取得する。
   * BE の `GET /v1/admin/departments` は openapi.json のとおり **素の配列**（`DepartmentResponse[]`）を返すが、
   * 過去に `{ data: [...] }` ラップを想定した呼び出し側（AdminUsers / AdminDepartments）が存在するため、
   * ここで両形式を吸収し常に `{ data: [...] }` に正規化して返す（課題 No.4 対応）。
   */
  list: async (token: string | null, params?: { include_inactive?: boolean }): Promise<DepartmentListResponse> => {
    const sp = new URLSearchParams();
    if (params?.include_inactive) sp.set('include_inactive', 'true');
    const q = sp.toString();
    const res = await api.get<DepartmentListResponse | DepartmentResponse[]>(
      `/v1/admin/departments${q ? `?${q}` : ''}`,
      token
    );
    const data = Array.isArray(res)
      ? res
      : Array.isArray((res as DepartmentListResponse)?.data)
        ? (res as DepartmentListResponse).data
        : [];
    return { data };
  },

  create: (body: DepartmentCreateRequest, token: string | null, idempotencyKey?: string) => {
    const key = idempotencyKey ?? crypto.randomUUID();
    return api.post<DepartmentResponse>('/v1/admin/departments', body, token, key);
  },

  update: (id: string, body: DepartmentUpdateRequest, token: string | null) =>
    api.put<DepartmentResponse>(`/v1/admin/departments/${encodeURIComponent(id)}`, body, token),

  /**
   * 論理削除。force=true で配下 user.department_id を NULL 化したうえで削除。
   * force=false で配下ユーザーあり時は 409 DEPARTMENT_HAS_USERS が返る
   */
  delete: (id: string, token: string | null, params?: { force?: boolean }) => {
    const sp = new URLSearchParams();
    if (params?.force) sp.set('force', 'true');
    const q = sp.toString();
    return api.delete<void>(
      `/v1/admin/departments/${encodeURIComponent(id)}${q ? `?${q}` : ''}`,
      token
    );
  },
};

// --- データ申請承認ワークフロー（2026-05-30 追加 / 課題管理表 #13）---

export type DataRequestType = 'deletion' | 'anonymization';
export type DataRequestStatus =
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'completed'
  | 'failed'
  | 'withdrawn'
  | string;

export interface DataRequestItem {
  id: string;
  type: DataRequestType | string;
  user_id: string;
  status: DataRequestStatus;
  scope: string;
  requested_at: string;
  approved_at?: string | null;
  approved_by?: string | null;
  rejected_at?: string | null;
  rejected_by?: string | null;
  rejection_reason?: string | null;
  completed_at?: string | null;
}

export interface DataRequestListResponse {
  data: DataRequestItem[];
}

export interface DataRequestRejectBody {
  reason: string;
}

export const adminDataRequestsApi = {
  list: (
    token: string | null,
    params?: {
      type?: DataRequestType;
      status?: DataRequestStatus;
      limit?: number;
      /** 非表示にした申請も含めるか（課題 #93）。行は消えていないので、true なら常に参照できる */
      includeHidden?: boolean;
    }
  ) => {
    const sp = new URLSearchParams();
    if (params?.type) sp.set('type', params.type);
    if (params?.status) sp.set('status', params.status);
    if (params?.limit != null) sp.set('limit', String(params.limit));
    if (params?.includeHidden) sp.set('include_hidden', 'true');
    const q = sp.toString();
    return api.get<DataRequestListResponse>(
      `/v1/admin/data-requests${q ? `?${q}` : ''}`,
      token
    );
  },

  approve: (
    type: DataRequestType,
    requestId: string,
    token: string | null,
    idempotencyKey?: string
  ) => {
    const key = idempotencyKey ?? crypto.randomUUID();
    return api.post<DataRequestItem>(
      `/v1/admin/data-requests/${type}/${encodeURIComponent(requestId)}/approve`,
      undefined,
      token,
      key
    );
  },

  reject: (
    type: DataRequestType,
    requestId: string,
    body: DataRequestRejectBody,
    token: string | null,
    idempotencyKey?: string
  ) => {
    const key = idempotencyKey ?? crypto.randomUUID();
    return api.post<DataRequestItem>(
      `/v1/admin/data-requests/${type}/${encodeURIComponent(requestId)}/reject`,
      body,
      token,
      key
    );
  },

  /** 管理者による撤回（課題 #93）。pending のみ。本人が退職した後の整理を想定 */
  withdraw: (type: DataRequestType, requestId: string, token: string | null, idempotencyKey?: string) =>
    api.post<AdminWithdrawResponse>(
      `/v1/admin/data-requests/${type}/${encodeURIComponent(requestId)}/withdraw`,
      undefined,
      token,
      idempotencyKey ?? crypto.randomUUID()
    ),

  /** 一覧から隠す（課題 #93）。却下済み・撤回済みのみ。行は消えない */
  hide: (type: DataRequestType, requestId: string, token: string | null, idempotencyKey?: string) =>
    api.post<AdminWithdrawResponse>(
      `/v1/admin/data-requests/${type}/${encodeURIComponent(requestId)}/hide`,
      undefined,
      token,
      idempotencyKey ?? crypto.randomUUID()
    ),
};

/** 撤回・非表示の応答（課題 #93） */
export interface AdminWithdrawResponse {
  id: string;
  type: DataRequestType | string;
  status: DataRequestStatus;
}

// --- 産業保健（Phase 4: HLT-S01〜S03）---

export interface CandidateResponse {
  anon_id: string;
  latest_score: number | null;
  risk_level: string;
  last_recorded_at: string | null;
  /**
   * 本人が詳細閲覧に同意しているか（課題 #26 / BE 2026-09-11 追加）。
   * false の対象者は個人詳細を開くと 403 CONSENT_REQUIRED になるため、
   * 一覧の時点で導線を出し分け、空振りのクリックを防ぐ。
   */
  consent_granted?: boolean;
  /** 不足している同意。個人詳細の 403 が返す missing_consents と同じ形 */
  missing_consents?: Array<{ purpose: string; data_type: string; label_ja: string }>;
}

/** リスクレベル算出のメタ情報（2026-05-30 追加 / 仕様 0422 §6 / 課題管理表 #19） */
export interface HealthcareRiskMeta {
  algorithm_version: string;
  cutoff_version: string;
  cutoff_value: number;
  window_days: number;
  data_volume_min: number;
  calculated_at: string;
  documentation_url: string;
  /**
   * risk_level を決める閾値（mcs_score 基準）。
   *
   * **p 値は高いほど状態が悪い**（`classify_condition_band` と同じ向き）:
   *   low:    score < medium_at_or_above
   *   medium: medium_at_or_above <= score < high_at_or_above
   *   high:   score >= high_at_or_above
   *
   * 旧名 `high_below` / `medium_below` は Phase A（0〜100・高いほど良い）の名残で
   * 向きが逆だった（BE #82 で廃止）。旧 dev-api 向けに読み取りだけ残している。
   */
  risk_level_thresholds: {
    /** この値以上で medium（＝low の上限） */
    medium_at_or_above?: number;
    /** この値以上で high */
    high_at_or_above?: number;
    /** @deprecated Phase A の名残。向きが逆で値も旧スケール（BE #82 で廃止） */
    high_below?: number;
    /** @deprecated 同上 */
    medium_below?: number;
    [k: string]: number | undefined;
  };
}

export interface CandidateListResponse {
  data: CandidateResponse[];
  risk_meta?: HealthcareRiskMeta;
}

export interface PersonDetailResponse {
  anon_id: string;
  consent_granted: boolean;
  mcs_timeseries: Array<Record<string, unknown>>;
  highlights: Array<Record<string, unknown>>;
  follow_status: string | null;
  /**
   * フォロー状況のメモ（最新の更新のときに書いたもの）。BE に依頼中（2026-09-30。高尾さん
   * 「見たいのに見られない」）。この項目を返さない BE では undefined（画面にメモの欄を出さない）
   */
  follow_notes?: string | null;
  /** フォロー状況を最後に更新した日時（BE に依頼中） */
  follow_updated_at?: string | null;
}

export interface FollowUpdateRequest {
  /**
   * フォロー状況（課題 #71 / BE PR #143 で 4 値に固定）。
   * not_started=未対応 / in_progress=対応中 / observing=経過観察 / done=完了。
   * 4 値以外は 400、遷移の表から外れると 409 FOLLOW_TRANSITION_NOT_ALLOWED
   */
  status: string;
  /** 備考（500 字まで） */
  notes?: string | null;
}

/** 面談記録の項目・選択肢の 1 つ（無効化された値は新しくは選べない） */
export interface InterviewRecordOption {
  code: string;
  label: string;
  disabled?: boolean;
}

/** 面談記録の項目（I01〜I13） */
export interface InterviewRecordItemSpec {
  key: string;
  item_id: string;
  label: string;
  kind: string;
  required: boolean;
  options: InterviewRecordOption[];
  exclusive?: string[];
  mutually_exclusive?: string[][];
  max_length?: number | null;
  note?: string | null;
}

/**
 * 面談記録の I01〜I13（課題 #71 / BE PR #143）。作成と修正で同じ形を送る。
 * 修正（PUT）では送らなかった項目は空になるため、すべて送る
 */
export interface InterviewRecordFields {
  /** I01 実施日時（ISO 8601） */
  recorded_at: string | null;
  format_code: string | null;
  trigger_code: string | null;
  topics: string[];
  sleep_level: string | null;
  workload_level: string | null;
  relationship_level: string | null;
  person_intentions: string[];
  actions_taken: string[];
  plan_level: string | null;
  next_follow_needed: string | null;
  /** I12 次回予定日（YYYY-MM-DD）。I11 が needed のときだけ */
  next_follow_date: string | null;
  notes: string | null;
}

export interface InterviewRecordCreateRequest extends InterviewRecordFields {
  /** 利用者 ID、または候補一覧の anon_id */
  user_id: string;
  /** 記録する面談予定の ID。予定なしで実施した面談は省く（1 件につき記録は 1 件まで） */
  interview_id?: string | null;
}

export interface InterviewRecord extends InterviewRecordFields {
  id: string;
  user_id: string;
  anon_id: string;
  interview_id: string | null;
  recorded_at: string;
  /** 保存した時点の選択肢の版 */
  options_version: number;
  created_by: string;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface InterviewRecordListResponse {
  data: InterviewRecord[];
}

/** 面談予定（一覧の 1 件）。record_id は記録済みなら面談記録の ID、未記録なら null */
export interface InterviewSchedule extends InterviewResponse {
  record_id?: string | null;
}

export interface InterviewScheduleListResponse {
  data: InterviewSchedule[];
}

/** GET /v1/healthcare/interview-records/options（課題 #71 / BE PR #143） */
export interface InterviewRecordOptionsResponse {
  options_version: number;
  items: InterviewRecordItemSpec[];
  /** フォロー状況の 4 値（表示名つき） */
  follow_statuses: InterviewRecordOption[];
  /** 今の値 → 選べる値。まだ状況の無い方は not_started から始まる */
  follow_transitions: Record<string, string[]>;
}

export interface FollowUpdateResponse {
  status: string;
}

export interface InterviewCreateRequest {
  user_id: string;
  scheduled_at: string;
  location?: string | null;
  meeting_url?: string | null;
  notes?: string | null;
  notify_user?: boolean;
}

export interface InterviewUpdateRequest {
  scheduled_at?: string | null;
  location?: string | null;
  meeting_url?: string | null;
  notes?: string | null;
  status?: string | null;
  notify_user?: boolean;
}

export interface InterviewResponse {
  id: string;
  user_id: string;
  scheduled_at: string;
  location: string | null;
  meeting_url: string | null;
  notes: string | null;
  status: string;
}

export const healthcareApi = {
  getCandidates: (token: string | null) =>
    api.get<CandidateListResponse>('/v1/healthcare/candidates', token),

  getPerson: (personId: string, token: string | null) =>
    api.get<PersonDetailResponse>(`/v1/healthcare/person/${encodeURIComponent(personId)}`, token),

  /** 面談記録の項目・選択肢と、フォロー状況の 4 値・遷移（BE PR #143）。古い BE では 404 */
  getInterviewRecordOptions: (token: string | null) =>
    api.get<InterviewRecordOptionsResponse>('/v1/healthcare/interview-records/options', token),

  /**
   * その方の面談記録（実施日時の新しい順。BE PR #143）。limit=1 で最新だけ（個人詳細の要約用）。
   * 閲覧は BE の監査ログに残る（管理者の監査ログには出ない）
   */
  listInterviewRecords: (personId: string, token: string | null, limit?: number) =>
    api.get<InterviewRecordListResponse>(
      `/v1/healthcare/person/${encodeURIComponent(personId)}/interview-records${limit ? `?limit=${limit}` : ''}`,
      token
    ),

  /**
   * その方の面談予定（予定日時の新しい順）。記録に結び付ける予定を選ぶために使う。
   * 古い BE では 404（そのときは予定なしで記録を作る）
   */
  listInterviews: (personId: string, token: string | null, limit?: number) =>
    api.get<InterviewScheduleListResponse>(
      `/v1/healthcare/person/${encodeURIComponent(personId)}/interviews${limit ? `?limit=${limit}` : ''}`,
      token
    ),

  /** 作成。入力の問題は 422 INVALID_INTERVIEW_RECORD（details.field_errors に項目ごと） */
  createInterviewRecord: (body: InterviewRecordCreateRequest, token: string | null, idempotencyKey?: string) =>
    api.post<InterviewRecord>('/v1/healthcare/interview-records', body, token, idempotencyKey ?? crypto.randomUUID()),

  /** 修正。I01〜I13 をすべて送る（対象の方と面談予定は変えられない） */
  updateInterviewRecord: (recordId: string, body: InterviewRecordFields, token: string | null) =>
    api.put<InterviewRecord>(`/v1/healthcare/interview-records/${encodeURIComponent(recordId)}`, body, token),

  updateFollowStatus: (personId: string, body: FollowUpdateRequest, token: string | null) =>
    api.put<FollowUpdateResponse>(`/v1/healthcare/person/${encodeURIComponent(personId)}/follow-status`, body, token),

  createInterview: (body: InterviewCreateRequest, token: string | null, idempotencyKey?: string) =>
    api.post<InterviewResponse>(
      '/v1/healthcare/interviews',
      body,
      token,
      idempotencyKey ?? crypto.randomUUID()
    ),

  updateInterview: (interviewId: string, body: InterviewUpdateRequest, token: string | null) =>
    api.put<InterviewResponse>(`/v1/healthcare/interviews/${encodeURIComponent(interviewId)}`, body, token),

  /** リスクレベル算出のメタ情報を取得（2026-05-30 追加） */
  getRiskMeta: (token: string | null) =>
    api.get<HealthcareRiskMeta>('/v1/healthcare/risk-meta', token),
};

// --- 通知・同意・設定（Phase 5: EMP-S04, S05, S06, S07）---

export interface NotificationResponse {
  id: string;
  title: string;
  message: string;
  level: string;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
}

export interface NotificationListResponse {
  data: NotificationResponse[];
  nextCursor?: string | null;
  hasMore?: boolean;
}

export interface NotificationSettingsResponse {
  checkin_enabled: boolean;
  feedback_enabled: boolean;
  message_enabled: boolean;
  reminder_time: string | null;
}

export interface NotificationSettingsUpdateRequest {
  checkin_enabled?: boolean | null;
  feedback_enabled?: boolean | null;
  message_enabled?: boolean | null;
  reminder_time?: string | null;
}

/**
 * GET /v1/consent の data[] の1要素（OpenAPI ConsentResponse と一致）。
 * フィールドの意味的な並び: …, status, granted_at, document_id, revoked_at, created_at。
 */
export interface ConsentResponse {
  id: string;
  purpose: string;
  data_type: string;
  version: string;
  /** API 契約: granted | revoked のみ */
  status: 'granted' | 'revoked';
  granted_at?: string | null;
  document_id?: string | null;
  revoked_at?: string | null;
  created_at?: string;
  /** 日本語ラベル（2026-05-30 BE 追加） */
  purpose_label_ja?: string;
  data_type_label_ja?: string;
}

/** GET /v1/consent/master のレスポンス（label_ja マスタ、2026-05-30 追加） */
export interface ConsentMasterLabel {
  code: string;
  label_ja: string;
}

export interface ConsentMasterResponse {
  purposes: ConsentMasterLabel[];
  data_types: ConsentMasterLabel[];
  consent_modes: ConsentMasterLabel[];
}

/** GET /v1/consent のレスポンス本体（data ラップ）。POST /v1/consent と /revoke は単一 ConsentResponse を返すため、呼び出し側で GET /v1/consent を再取得する */
export interface ConsentListResponse {
  data: ConsentResponse[];
}

export interface ConsentRequest {
  purpose: string;
  data_type: string;
  version: string;
  document_id?: string | null;
}

export interface ConsentRevokeRequest {
  purpose: string;
  data_type: string;
  version: string;
  document_id?: string | null;
}

/**
 * 同意モード。`anonymous` / `personal_identified` は排他、`common` は両モード共通で必須。
 * ユーザーは同時に 1 モードのみに属し、personal_identified は anonymous のスーパーセット
 */
export type ConsentMode = 'anonymous' | 'personal_identified' | 'common';

/**
 * GET /v1/consent/documents の配列要素（レスポンスは **配列そのまま**。ConsentListResponse の data ラップとは異なる）。
 * content は null を返さず、本文なしは空文字 ""。
 * 2026-04-17 以降は consent_mode フィールドが同梱される（未同梱の場合は common 相当として扱う）
 */
export interface ConsentDocumentResponse {
  id: string;
  version: string;
  title: string;
  content: string;
  effective_from: string;
  consent_mode?: ConsentMode;
  purpose?: string;
  data_type?: string;
  /** 日本語ラベル（2026-05-30 BE 追加。未同梱の場合はクライアントで翻訳） */
  purpose_label_ja?: string;
  data_type_label_ja?: string;
  consent_mode_label_ja?: string;
}

/** GET /v1/consent/effective の 1 要素（各 purpose/data_type/version ごとの有効同意状態） */
export interface ConsentEffectiveItem {
  purpose: string;
  data_type: string;
  version: string;
  effective_status: 'granted' | 'revoked' | 'pending' | string;
  consent_id?: string | null;
  document_id?: string | null;
  granted_at?: string | null;
  revoked_at?: string | null;
  created_at?: string;
}

export interface ConsentEffectiveResponse {
  items: ConsentEffectiveItem[];
}

/**
 * GET /v1/consent/master-modes のレスポンス。
 * モード別に公開中の同意文書を返し、クライアントはモード切替 UI で
 * 「このモードに切り替えると以下の文書に同意することになります」と一覧表示できる
 */
export interface ConsentMasterModesDocument {
  id: string;
  purpose: string;
  data_type: string;
  version: string;
  title: string;
  effective_from: string;
  consent_mode?: ConsentMode;
  /** 日本語ラベル（2026-05-30 BE 追加） */
  purpose_label_ja?: string;
  data_type_label_ja?: string;
}

export interface ConsentMasterModesResponse {
  anonymous: ConsentMasterModesDocument[];
  personal_identified: ConsentMasterModesDocument[];
  common: ConsentMasterModesDocument[];
}

/**
 * テナント（組織）単位の同意モード。2026-04-17 の設計変更で、個人単位ではなくテナント単位で管理する方式に一本化された。
 * `common` は文書側にのみ存在し、テナントモードとしては使われない
 */
export type TenantConsentMode = 'anonymous' | 'personal_identified';

/** GET /v1/organization/consent-mode（全ユーザー可）のレスポンス */
export interface TenantConsentModeResponse {
  organization_id: string;
  tenant_code: string;
  name: string;
  consent_mode: TenantConsentMode;
  /**
   * 呼び出した本人の録音で、文字起こし（音声認識）を行うか（課題 #99 / BE PR #153）。
   * false・項目が無いときは、文字起こしに関わる案内と欄を出さない（行わない側に倒す）
   */
  transcription_enabled?: boolean;
}

/** PUT /v1/admin/organization/consent-mode リクエスト（admin のみ） */
export interface TenantConsentModeSwitchRequest {
  mode: TenantConsentMode;
}

/** PUT /v1/admin/organization/consent-mode の anonymization_queued 要素 */
export interface TenantConsentModeAnonymizationQueueItem {
  user_id: string;
  anonymization_request_id: string;
}

/**
 * PUT /v1/admin/organization/consent-mode のレスポンス。
 * personal_identified → anonymous 時は全ユーザー分の DataAnonymizationRequest が
 * `status=approved` で自動投入され、バックグラウンドワーカー（5 分間隔）が匿名化を実行する
 */
export interface TenantConsentModeSwitchResponse {
  organization_id: string;
  consent_mode: TenantConsentMode;
  previous_consent_mode: TenantConsentMode;
  /** 同一モードへの no-op は false */
  changed: boolean;
  /** revoke された同意イベントの合計件数 */
  revoked_consent_count: number;
  /** 匿名化申請が投入されたユーザー（personal_identified → anonymous 時のみ非空） */
  anonymization_queued: TenantConsentModeAnonymizationQueueItem[];
}

export interface DataDeletionRequestBody {
  scope?: 'all' | 'last_30_days';
}

export interface DataDeletionResponse {
  id: string;
  status: string;
  scope: string;
  requested_at: string;
  approved_at?: string | null;
  rejected_at?: string | null;
  completed_at?: string | null;
}

export const notificationsApi = {
  list: (token: string | null, params?: { limit?: number; cursor?: string | null }) => {
    const sp = new URLSearchParams();
    if (params?.limit != null) sp.set('limit', String(params.limit));
    if (params?.cursor != null) sp.set('cursor', params.cursor);
    const q = sp.toString();
    return api.get<NotificationListResponse>(`/v1/notifications${q ? `?${q}` : ''}`, token);
  },
  markRead: (notificationId: string, token: string | null, idempotencyKey?: string) =>
    api.post<unknown>(
      `/v1/notifications/${encodeURIComponent(notificationId)}/read`,
      undefined,
      token,
      idempotencyKey ?? crypto.randomUUID()
    ),
};

export const notificationSettingsApi = {
  get: (token: string | null) =>
    api.get<NotificationSettingsResponse>('/v1/user/notification-settings', token),
  update: (body: NotificationSettingsUpdateRequest, token: string | null) =>
    api.put<NotificationSettingsResponse>('/v1/user/notification-settings', body, token),
};

/**
 * `GET /v1/public/consent-documents` の文書要素（2026-04-17〜）。
 * 未認証エンドポイントから返される招待受諾画面向け文書
 */
export interface PublicConsentDocument {
  id: string;
  purpose: string;
  data_type: string;
  version: string;
  title: string;
  content: string;
  consent_mode: ConsentMode;
  effective_from: string;
  /** 日本語ラベル（2026-05-30 BE 追加） */
  purpose_label_ja?: string;
  data_type_label_ja?: string;
  consent_mode_label_ja?: string;
}

export interface PublicConsentDocumentsResponse {
  tenant_code: string;
  organization_id: string;
  consent_mode: TenantConsentMode;
  documents: PublicConsentDocument[];
}

/**
 * 未認証で取得する招待受諾画面用の同意文書。
 * 条件: 指定テナントに属し、`status='published'` かつ `show_on_invite_page=true`
 * かつ `consent_mode IN ('common', 'anonymous')`。レート制限: 60 秒あたり 20 req
 */
/**
 * 本文を開いた文書（課題 #77 / BE PR #130）。開いた時刻（viewed_at）はサーバーの時刻で、
 * 同意の記録に結び付く。同意にはこの版（version）を送る。
 */
export interface OpenedConsentDocument {
  id: string;
  title: string;
  version: string;
  content: string;
  effective_from: string;
  viewed_at: string;
  purpose?: string;
  data_type?: string;
  purpose_label_ja?: string;
  data_type_label_ja?: string;
}

export const publicConsentApi = {
  getByTenant: (tenantCode: string) => {
    const q = new URLSearchParams({ tenant_code: tenantCode });
    return api.get<PublicConsentDocumentsResponse>(
      `/v1/public/consent-documents?${q.toString()}`
    );
  },
  /**
   * 招待の受け入れ画面で本文を開く（BE PR #130）。開いた記録が招待に結び付き、受け入れ
   * （accept-invite）の同意に必要になる。招待のトークンは URL ではなくヘッダで送る（ログに残さない）。
   */
  openDocument: (documentId: string, inviteToken: string) =>
    api.get<OpenedConsentDocument>(
      `/v1/public/consent-documents/${encodeURIComponent(documentId)}`,
      null,
      { 'X-Invite-Token': inviteToken }
    ),
};

/** 招待の中身（受け入れの前に読む。課題 #99） */
export interface PublicInvite {
  /** 招待の役割（admin / healthcare / employee） */
  role: string;
  expires_at?: string;
}

export const publicInviteApi = {
  /**
   * 招待の役割を読む（課題 #99）。同意文書がまだ無い組織で、管理者・産業保健職が同意なしで
   * アカウントを作れるかを決める。招待のトークンは URL ではなくヘッダで送る（ログに残さない）。
   * この API が無い古い BE では 404 になる。
   */
  get: (inviteToken: string) =>
    api.get<PublicInvite>('/v1/public/invite', null, { 'X-Invite-Token': inviteToken }),
};

export const consentApi = {
  /**
   * 本文を開く（BE PR #130）。開いた記録（サーバーの時刻）が残り、同意（grant）に必要になる。
   * 文書が改訂されると版が変わるので、新しい版を開き直すまで同意できない（409 DOCUMENT_NOT_OPENED）。
   */
  openDocument: (documentId: string, token: string | null) =>
    api.get<OpenedConsentDocument>(`/v1/consent/documents/${encodeURIComponent(documentId)}`, token),
  list: (token: string | null) =>
    api.get<ConsentListResponse>('/v1/consent', token),
  /**
   * 同意を付与する。
   *
   * **Idempotency-Key はここで自動採番する**（課題 #79）。
   * 以前は呼び出し側が任意で渡す設計だったが、2 画面とも渡しておらず
   * ヘッダが付いていなかった。呼び出し側の渡し忘れを構造的に防ぐため、
   * users.invite / departments.create 等と同じく API 層で採番する方式に揃える。
   *
   * BE は現在 /v1/consent を SKIP_IDEMPOTENCY_PATHS に入れておりヘッダを無視するが、
   * FE のリリース後に BE 側でスキップを解除する段取り。
   * **逆順（BE 先行）だと現行 FE からの同意付与が全て 400 になる**ため、この順序が必須。
   */
  grant: (body: ConsentRequest, token: string | null, idempotencyKey?: string) => {
    const key = idempotencyKey ?? crypto.randomUUID();
    return api.post<ConsentResponse>('/v1/consent', body, token, key);
  },
  /** 同意を撤回する。grant と同じ理由で Idempotency-Key を自動採番する（課題 #79） */
  revoke: (body: ConsentRevokeRequest, token: string | null, idempotencyKey?: string) => {
    const key = idempotencyKey ?? crypto.randomUUID();
    return api.post<unknown>('/v1/consent/revoke', body, token, key);
  },
  getDocuments: (token: string | null) =>
    api.get<ConsentDocumentResponse[]>('/v1/consent/documents', token),
  /** GET /v1/consent/effective — 機能別に有効な同意状況を返す（2026-04-17〜） */
  getEffective: (token: string | null) =>
    api.get<ConsentEffectiveResponse>('/v1/consent/effective', token),
  /** GET /v1/consent/master-modes — モード別の公開中文書グループを返す（2026-04-17〜） */
  getMasterModes: (token: string | null) =>
    api.get<ConsentMasterModesResponse>('/v1/consent/master-modes', token),
  /** GET /v1/consent/master — 英語コード→日本語ラベルのマスタを返す（2026-05-30 追加） */
  getMaster: (token: string | null) =>
    api.get<ConsentMasterResponse>('/v1/consent/master', token),
};

/**
 * テナント（組織）単位の同意モード管理（2026-04-17〜）。
 * - `getSelf`（全ユーザー可）: 自分のテナントの現モードを取得
 * - `adminGet`（admin のみ）: 同上。admin ロールを明示したい場合
 * - `adminSwitch`（admin のみ）: テナント全体のモードを切替。
 *   `personal_identified → anonymous` 時は全ユーザー分の匿名化申請が自動投入される
 *
 * 旧 `POST /v1/user/consent-mode` は廃止された（個人単位のモード選択は無効）
 */
export const organizationConsentModeApi = {
  getSelf: (token: string | null) =>
    api.get<TenantConsentModeResponse>('/v1/organization/consent-mode', token),

  adminGet: (token: string | null) =>
    api.get<TenantConsentModeResponse>('/v1/admin/organization/consent-mode', token),

  adminSwitch: (
    body: TenantConsentModeSwitchRequest,
    token: string | null,
    idempotencyKey?: string
  ) => {
    const key = idempotencyKey ?? crypto.randomUUID();
    return api.put<TenantConsentModeSwitchResponse>(
      '/v1/admin/organization/consent-mode',
      body,
      token,
      key
    );
  },
};

export const dataDeletionApi = {
  create: (body: DataDeletionRequestBody, token: string | null, idempotencyKey?: string) =>
    api.post<DataDeletionResponse>(
      '/v1/data-deletion',
      body,
      token,
      idempotencyKey ?? crypto.randomUUID()
    ),
  get: (requestId: string, token: string | null) =>
    api.get<DataDeletionResponse>(`/v1/data-deletion/${encodeURIComponent(requestId)}`, token),
  /**
   * 本人による撤回（課題 #93）。pending のみ（それ以外は 409）。
   * BE は本人の申請に限って受け付ける。画面からは自分の申請の一覧からだけ呼び、
   * 申請 ID を入力させる導線は作らない。
   */
  withdraw: (requestId: string, token: string | null, idempotencyKey?: string) =>
    api.post<DataDeletionResponse>(
      `/v1/data-deletion/${encodeURIComponent(requestId)}/withdraw`,
      undefined,
      token,
      idempotencyKey ?? crypto.randomUUID()
    ),
};

// --- 同意文書管理（管理者向け、ADM-F26〜F31 / 要件 9.2）---

export type ConsentDocumentStatus = 'draft' | 'published' | 'archived';

/**
 * 管理者向け同意文書（詳細形、`content` を含む）。
 * 一覧レスポンスでは `content` を含まない `ConsentDocumentSummary` を使う
 */
export interface ConsentDocumentAdmin {
  id: string;
  purpose: string;
  data_type: string;
  /** semver "MAJOR.MINOR.PATCH" */
  version: string;
  title: string;
  /** Markdown 本文 */
  content: string;
  /** ISO8601 UTC */
  effective_from: string;
  status: ConsentDocumentStatus;
  created_at: string;
  created_by: string;
  updated_at: string;
  updated_by: string;
  published_at: string | null;
  archived_at: string | null;
  /** 前版の id（新 version 作成時） */
  parent_id: string | null;
  /** 同意モード（2026-04-17〜）。省略時は common 相当として扱う。draft のみ更新可 */
  consent_mode?: ConsentMode;
  /**
   * 招待受諾画面で表示するかのフラグ（2026-04-17〜）。
   * **例外的に published / archived 状態でも切替可**（表示設定のため UX 配慮）
   */
  show_on_invite_page?: boolean;
}

export type ConsentDocumentSummary = Omit<ConsentDocumentAdmin, 'content'>;

export interface ConsentDocumentListResponse {
  data: ConsentDocumentSummary[];
}

export interface ConsentDocumentCreateRequest {
  purpose: string;
  data_type: string;
  version: string;
  title: string;
  content: string;
  effective_from: string;
  parent_id?: string | null;
  /** 同意モード（省略時は common） */
  consent_mode?: ConsentMode;
  /** 招待受諾画面に表示するか（省略時は false） */
  show_on_invite_page?: boolean;
}

export interface ConsentDocumentUpdateRequest {
  title?: string;
  content?: string;
  version?: string;
  effective_from?: string;
  /** 同意モード（draft 状態のみ更新可） */
  consent_mode?: ConsentMode;
  /** 招待受諾画面に表示するか（published / archived 状態でも切替可） */
  show_on_invite_page?: boolean;
}

export interface ConsentDocumentPublishRequest {
  require_reconsent: boolean;
  notify_users: boolean;
}

export interface ConsentDocumentArchiveRequest {
  reason?: string;
}

export interface ConsentDocumentListParams {
  status?: ConsentDocumentStatus[];
  purpose?: string;
  data_type?: string;
  include_archived?: boolean;
  /** 同意モードで絞り込み（2026-04-17〜） */
  consent_mode?: ConsentMode;
}

/**
 * 同意文書マスタ（purpose / data_type / consent_mode の許可値）レスポンス。
 * BE 確定仕様（2026-04-14〜04-17）: トップレベルは `purposes` / `data_types`（複数形、string[]）、
 * 2026-04-17 以降は `consent_modes` も同梱される
 */
export interface ConsentDocumentMasterResponse {
  purposes: string[];
  data_types: string[];
  consent_modes?: string[];
}

export interface ConsentDocumentMaster {
  purposes: string[];
  data_types: string[];
  consent_modes: ConsentMode[];
}

const CONSENT_MODE_VALUES: readonly ConsentMode[] = ['anonymous', 'personal_identified', 'common'];

function isConsentMode(v: unknown): v is ConsentMode {
  return typeof v === 'string' && (CONSENT_MODE_VALUES as readonly string[]).includes(v);
}

/** レスポンスが unknown 形だった場合の防御として、配列と要素型を検証する */
export function normalizeConsentDocumentMaster(
  res: ConsentDocumentMasterResponse | null | undefined
): ConsentDocumentMaster {
  const purposes = Array.isArray(res?.purposes)
    ? res.purposes.filter((p): p is string => typeof p === 'string')
    : [];
  const dataTypes = Array.isArray(res?.data_types)
    ? res.data_types.filter((d): d is string => typeof d === 'string')
    : [];
  const consentModes = Array.isArray(res?.consent_modes)
    ? res.consent_modes.filter(isConsentMode)
    : [...CONSENT_MODE_VALUES];
  return { purposes, data_types: dataTypes, consent_modes: consentModes };
}

export const adminConsentDocumentsApi = {
  getMaster: (token: string | null) =>
    api.get<ConsentDocumentMasterResponse>('/v1/admin/consent-documents/master', token),

  list: (token: string | null, params?: ConsentDocumentListParams) => {
    const sp = new URLSearchParams();
    if (params?.status && params.status.length > 0) sp.set('status', params.status.join(','));
    if (params?.purpose) sp.set('purpose', params.purpose);
    if (params?.data_type) sp.set('data_type', params.data_type);
    if (params?.include_archived) sp.set('include_archived', 'true');
    if (params?.consent_mode) sp.set('consent_mode', params.consent_mode);
    const q = sp.toString();
    return api.get<ConsentDocumentListResponse>(
      `/v1/admin/consent-documents${q ? `?${q}` : ''}`,
      token
    );
  },

  get: (id: string, token: string | null) =>
    api.get<ConsentDocumentAdmin>(`/v1/admin/consent-documents/${encodeURIComponent(id)}`, token),

  create: (body: ConsentDocumentCreateRequest, token: string | null, idempotencyKey?: string) => {
    const key = idempotencyKey ?? crypto.randomUUID();
    return api.post<ConsentDocumentAdmin>('/v1/admin/consent-documents', body, token, key);
  },

  update: (id: string, body: ConsentDocumentUpdateRequest, token: string | null) =>
    api.put<ConsentDocumentAdmin>(
      `/v1/admin/consent-documents/${encodeURIComponent(id)}`,
      body,
      token
    ),

  publish: (
    id: string,
    body: ConsentDocumentPublishRequest,
    token: string | null,
    idempotencyKey?: string
  ) => {
    const key = idempotencyKey ?? crypto.randomUUID();
    return api.post<ConsentDocumentAdmin>(
      `/v1/admin/consent-documents/${encodeURIComponent(id)}/publish`,
      body,
      token,
      key
    );
  },

  archive: (
    id: string,
    body: ConsentDocumentArchiveRequest,
    token: string | null,
    idempotencyKey?: string
  ) => {
    const key = idempotencyKey ?? crypto.randomUUID();
    return api.post<ConsentDocumentAdmin>(
      `/v1/admin/consent-documents/${encodeURIComponent(id)}/archive`,
      body,
      token,
      key
    );
  },

  /**
   * 下書き（draft）を論理削除する（課題 No.48 / BE 2026-08-25 追加）。
   * - draft のみ 204。published / archived は **409 `INVALID_STATE_TRANSITION`**
   *   （公開済みは同意取得の根拠＝存在自体が証跡のため削除不可。撤去は archive を使う）。
   * - 存在しない ID / admin 以外は 404。
   * - BE の Idempotency-Key ミドルウェアは POST のみが対象のためキーは送らない。
   */
  delete: (id: string, token: string | null) =>
    api.delete<void>(`/v1/admin/consent-documents/${encodeURIComponent(id)}`, token),
};

/**
 * master API 取得失敗時のフォールバック定数（BE 確定 2026-04-14 の `consent_document_master` と一致）。
 * 通常は `adminConsentDocumentsApi.getMaster()` の結果を優先して使う
 */
export const CONSENT_DOCUMENT_PURPOSE_OPTIONS = [
  'analysis',
  'anonymous_aggregate',
  'personal_view',
  'healthcare_detail',
  'legacy',
] as const;

export const CONSENT_DOCUMENT_DATA_TYPE_OPTIONS = [
  'voice',
  'mcs',
  'personal_info',
  'legacy',
] as const;

// --- ファイルアップロード・音声解析ジョブ（EMP-S08 音声記録用）---

/** POST /v1/files/presign リクエスト */
export interface PresignRequest {
  filename: string;
  content_type?: string | null;
  size_bytes?: number | null;
  checksum_sha256?: string | null;
}

/** POST /v1/files/presign レスポンス */
export interface PresignResponse {
  file_key: string;
  upload_url: string;
  expires_in: number;
}

/** POST /v1/files/complete リクエスト */
export interface CompleteRequest {
  file_key: string;
  size_bytes?: number | null;
  checksum_sha256?: string | null;
}

/** POST /v1/files/complete レスポンス */
export interface CompleteResponse {
  status: string;
  completed_at: string | null;
}

/** POST /v1/voice-jobs リクエスト */
export interface CreateVoiceJobRequest {
  file_key: string;
  duration: number;
  locale?: string;
  /** 未指定時はバックエンドの有効版選択ロジックに委譲 */
  prompt_version?: string;
  model_version: string;
  retention_policy_id?: string | null;
  /** 同期モード: true で解析完了まで待ち response.recording.analysis_json を返す（タイムアウト 90 秒推奨） */
  sync?: boolean;
  /**
   * 録音開始時に取得したお題の ID（API #91〜）。取得しなかった場合は未指定。
   */
  topic_id?: string | null;
  /**
   * お題カードが**実際に画面に表示された**か。prompted / unprompted の判定はこれで行う。
   * 取得しても詰まらなかった方には表示されないため、topic_id の有無とは一致しない。
   */
  topic_shown?: boolean;
  /** 「別のお題」で引き直したか */
  topic_redrawn?: boolean;
  /**
   * お題・追い質問の音声の流れ方（課題 #160）。お題・追い質問を出さなかったときは送らない
   * （値の意味は utils/topicAudio.ts）
   */
  topic_audio?: TopicAudioOutcome;
  follow_up_audio?: TopicAudioOutcome;
}

/** お題の音声の流れ方（課題 #160。POST /v1/voice-jobs の topic_audio・follow_up_audio） */
export type TopicAudioOutcome = 'completed' | 'interrupted' | 'failed' | 'muted' | 'no_audio';

/** POST /v1/voice-jobs レスポンス */
export interface VoiceJobResponse {
  id: string;
  status: string;
  file_key: string;
  prompt_version: string;
  model_version: string;
  engine_version?: string | null;
  created_at: string;
  updated_at: string;
  /** 失敗時（status === "failed"）にメッセージ */
  error_message?: string | null;
}

/** 同期モード時の recording（解析結果）。recording.id は詳細取得などに利用可能 */
export interface VoiceJobRecording {
  id?: string;
  analysis_json?: unknown;
  recorded_at?: string;
  /**
   * 音声認識テキスト（本人のみ閲覧可）。2026-04-17 〜。
   * 録音から 6 か月後に BE ワーカーが自動で NULL 化する
   */
  transcript_text?: string | null;
}

export interface CreateVoiceJobResponse {
  job: VoiceJobResponse;
  /** 同期モード時のみ。解析結果をそのまま画面表示に利用 */
  recording?: VoiceJobRecording | null;
  message?: string | null;
  /**
   * この録音で文字起こしを行ったか（課題 #99 / BE PR #153）。true のときだけ、音声認識テキストの欄を出す。
   * false・項目が無いときは、テキストが返っても出さない
   */
  transcription_enabled?: boolean;
}

export const filesApi = {
  presign: (body: PresignRequest, token: string | null, idempotencyKey?: string) =>
    api.post<PresignResponse>('/v1/files/presign', body, token, idempotencyKey),

  complete: (body: CompleteRequest, token: string | null, idempotencyKey?: string) =>
    api.post<CompleteResponse>('/v1/files/complete', body, token, idempotencyKey),
};

const VOICE_JOB_SYNC_TIMEOUT_MS = 90_000;

export const voiceJobsApi = {
  create: (
    body: CreateVoiceJobRequest,
    token: string | null,
    idempotencyKey?: string,
    timeoutMs?: number
  ) =>
    api.post<CreateVoiceJobResponse>(
      '/v1/voice-jobs',
      body,
      token,
      idempotencyKey,
      timeoutMs
    ),

  /** 同期モード用: 90 秒タイムアウトで create を呼ぶ */
  createSync: (
    body: CreateVoiceJobRequest,
    token: string | null,
    idempotencyKey?: string
  ) =>
    api.post<CreateVoiceJobResponse>(
      '/v1/voice-jobs',
      { ...body, sync: true },
      token,
      idempotencyKey,
      VOICE_JOB_SYNC_TIMEOUT_MS
    ),

  get: (jobId: string, token: string | null) =>
    api.get<VoiceJobResponse>(`/v1/voice-jobs/${jobId}`, token),
};

/**
 * 録音中に出す「お題」（API #89 / 課題 #60 第 1 段階）。
 *
 * BE は同じ日に同じ問いを返さない。取得するたびにプールを 1 件消費するため、
 * 画面表示時などに先読みしてはいけない（録音しない人にも消費される）。
 * 呼ぶのは録音開始の瞬間だけ。
 */
export interface VoiceTopicResponse {
  topic_id: string;
  /** 画面に出す問い。1 行で表示する想定 */
  question: string;
  /** daily / imagination */
  category: string;
  /**
   * 20〜30 秒に届かなかったときに継ぎ足す追い質問。
   * お題と同時に返るので、必要になった時点で FE が表示する
   * （無音が続いている最中に通信待ちを発生させないため）。
   */
  follow_up?: string | null;
  /**
   * お題を読み上げた音声（MP3）の URL（課題 #160。BE 84bef41）。期限つき（既定 1 時間）。
   * 音声が無いお題・流さない設定のあいだは null（FE は文字だけで出し、送信の topic_audio は no_audio）。
   * 音声は AI で作ったもので、お題のカードに「お題の音声は AI で作成しています」と添える
   */
  question_audio_url?: string | null;
  /** 追い質問を読み上げた音声（MP3）の URL。扱いは question_audio_url と同じ */
  follow_up_audio_url?: string | null;
}

export const voiceTopicsApi = {
  /**
   * 次のお題を 1 件取得する。出題できるお題が無い場合 BE は 404 を返す。
   * その場合はカードを出さずに録音を続ける（お題は録音の必須要素ではない）。
   *
   * `redraw` は「別のお題」ボタンからの取得かどうか。BE は回数を制限しないので、
   * 1 録音につき 1 回という制限は FE 側で行う。
   */
  next: (token: string | null, redraw = false) =>
    api.get<VoiceTopicResponse>(
      `/v1/voice-topics/next${redraw ? '?redraw=true' : ''}`,
      token
    ),
};
